/**
 * LeadDetailPage — one scrolling client page with a sticky header and three
 * tabs (Estimate / Sales / Dispatch), matching the reference CRM (V5.5).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { tintOf } from '../lib/theme';
import { numberInput, STEP_PER_LB } from '../lib/numberInput';
import { useApi, useApiClient } from '../hooks/useApi';
import { useRowSync } from '../hooks/useRowSync';
import { useAction } from '../hooks/useAction';
import { usePhoneWidth } from '../hooks/usePhoneWidth';
import { useDraftText, draftKey, DraftStatus } from '../hooks/useDraftText';
import { useAuth } from '../contexts/AuthContext';
import { STAGES, stageColor, stageBg } from '../lib/stages';
import { formatDate, formatDateTime, getInitials, formatMoney } from '../lib/utils';
import { DEFAULT_TZ, todayInTz } from '../lib/time';
import { calcEstimate, CalcLine, DiscountType, accessorialKey, roundUpToQuarterHour, findAdditionalTruckFee } from '../lib/estimate';
import { InlineInput, HoursMinutesInput, hmShort, cardStyle, cardTitle, fieldLabel, unitLabel } from './TimeInputs';
import { BillingSections } from './BillingSections';
import { FinalizeTab } from './FinalizeTab';
import { InventoryPanel } from './InventoryPanel';
import { DispatchTab } from './DispatchTab';
import { CoveragePanel, CoverageSave } from './CoveragePanel';
import { PhoneLink } from './PhoneLink';
import { OptedOutBadge } from './TextCompose';
import { ringCentralPhoneEnabled, ringCentralAppCalls, startCall, callWithRingCentralApp } from '../lib/ringcentral';
import { EstimateSendConfirm } from './EstimateSendConfirm';
import { ActivityLog } from './ActivityLog';
import { FeedbackLine } from './FeedbackLine';
import { SendHistory } from './SendHistory';
import { EditContactModal } from './Modals';
import { Icons } from '../lib/icons';
import { formatJobNo, jobNo } from '../lib/jobNumber';
import { formatPhone, telHref } from '../lib/format';
import { dvpQuote, dvpOffered, dvpMoney, DVP_NAME } from '../lib/dvp';
import { isLongDistance } from '../lib/moveType';
import { MOVE_SIZES } from '../lib/moveSizes';
import { Leg, AddressParts, PROVINCES, isCanada, normalizePostal, partsOf, splitAddress, addressColumns, needsCheck } from '../lib/addressParts';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

const MOVE_TYPES = ['Long Distance Relocation', 'Local Move'];
const FALLBACK_SOURCES = ['MoveIt', 'MyMovingLoads', 'N1M Website', 'Baltic Website', 'Referral', 'Google', 'Other'];
const FALLBACK_ARRIVAL_WINDOWS = ['8am - 9:30am', '9am - 11am', '10am - 12pm', '12pm - 2pm', '1pm - 4pm', '2pm - 5pm'];

// G1b (revised): there is NO Email tab. Email lives in the Sales activity feed,
// one entry per message in time order with the notes — a rep should not have to
// check two surfaces to learn what happened on one file. App still owns
// /lead/:id?tab=… and validates against this array; it maps the retired
// ?tab=email onto 'sales' so yesterday's links and bookmarks land on the feed.
/**
 * HEADER BUTTONS — ONE TREATMENT (Batch 0 of the polish pass).
 *
 * This header carried eight buttons in four different border colours — grey,
 * blue, amber, grey again — with no rule saying what a colour meant. Colour was
 * decoration, so it read as meaning and there was none to read: "Email estimate"
 * being blue and "Preview portal" amber told you nothing about either.
 *
 * Two variants now, and the distinction is real: ONE primary action per header
 * (the thing a rep most often came here to do), everything else secondary.
 * Destructive actions stay red where they exist elsewhere in the file.
 *
 * Local to this component on purpose. The shared <Button> is Batch 2; hoisting
 * it now would mean changing every screen two days before the demo.
 */
const HDR_BTN: React.CSSProperties = {
  padding: '6px 12px',
  background: 'var(--crm-panel-2)',
  border: '1px solid var(--crm-line)',
  color: 'var(--crm-muted)',
  borderRadius: '7px',
  cursor: 'pointer',
  fontSize: '12px',
  lineHeight: '16px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  whiteSpace: 'nowrap',
};
const HDR_BTN_PRIMARY: React.CSSProperties = {
  ...HDR_BTN,
  border: '1px solid var(--crm-accent-line)',
  color: 'var(--crm-accent)',
  fontWeight: 600,
};
/** Busy/disabled is one look everywhere, rather than four opacity values. */
const hdrBtn = (busy = false, primary = false): React.CSSProperties => ({
  ...(primary ? HDR_BTN_PRIMARY : HDR_BTN),
  cursor: busy ? 'default' : 'pointer',
  opacity: busy ? 0.55 : 1,
});

/**
 * The header's action menu.
 *
 * Five buttons sat across the top of every lead file — Inventory, Download
 * estimate PDF, Email estimate, Preview portal, Request inventory — and on a
 * laptop they wrapped onto a second row and pushed the tabs down. They are also
 * two jobs, not five: three things you do with the estimate and two with the
 * inventory. Grouped that way, the header is one row at any width and the
 * labels stop competing with the stage and rep controls, which are the things a
 * rep actually changes.
 *
 * Same mechanics as the Documents generate picker: a fixed click-catcher behind
 * an absolutely positioned panel, so clicking anywhere else closes it. Escape
 * closes it too — the catcher cannot receive a key.
 */
function HeaderMenu({ label, items, disabled }: {
  label: string;
  disabled?: boolean;
  items: { label: string; onClick: () => void; disabled?: boolean; title?: string }[];
}) {
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{ ...hdrBtn(disabled), display: 'inline-flex', alignItems: 'center', gap: '6px' }}
      >
        {label}
        <span aria-hidden="true" style={{ fontSize: '9px', opacity: 0.8 }}>▾</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div
            role="menu"
            style={{ position: 'absolute', top: '30px', left: 0, zIndex: 41, minWidth: '208px', background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '10px', padding: '6px', boxShadow: '0 16px 40px var(--crm-shadow-color)' }}
          >
            {items.map((it) => (
              <button
                key={it.label}
                role="menuitem"
                title={it.title}
                disabled={it.disabled}
                onClick={() => { setOpen(false); it.onClick(); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
                  background: 'none', border: 'none', borderRadius: '6px',
                  color: it.disabled ? 'var(--crm-dim)' : 'var(--crm-text)', fontSize: '12px',
                  cursor: it.disabled ? 'default' : 'pointer', whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => { if (!it.disabled) e.currentTarget.style.background = 'var(--crm-panel-2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
              >
                {it.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export type LeadTab = 'estimate' | 'sales' | 'dispatch' | 'finalize';
export const LEAD_TABS: LeadTab[] = ['estimate', 'sales', 'dispatch', 'finalize'];

interface LeadDetailPageProps {
  leadId: string;
  onBack: () => void;
  onOpenModal: (type: string, data?: any) => void;
  // Sub-tab is URL-owned by App (/lead/:id?tab=…) so it survives refresh/back.
  activeTab: LeadTab;
  onChangeTab: (tab: LeadTab) => void;
  /** G1d: scroll the Sales feed to one email and expand it. */
  focusMessage?: { messageId: string; n: number } | null;
}

/**
 * PHONE HEADER. On a phone the sticky header (name, contact, stage / rep / size /
 * source controls, move date, tabs) filled the screen, leaving nothing of the
 * file to scroll. At phone width it collapses to one line — name, job #, stage —
 * with a toggle; open or shut is remembered per user. Desktop is unchanged.
 */
const headerOpenKey = (userId: unknown) => `moveflow:leadHeaderOpen:${userId ?? 'anon'}`;
function readHeaderOpen(key: string): boolean {
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}

export function LeadDetailPage({ leadId, onBack, onOpenModal, activeTab: rawTab, onChangeTab, focusMessage }: LeadDetailPageProps) {
  const api = useApiClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin'; // Dispatch (contractor) is admin-only (N2)
  const phone = usePhoneWidth();
  const headerKey = headerOpenKey(user?.id);
  const [headerOpen, setHeaderOpenState] = useState(() => readHeaderOpen(headerKey));
  useEffect(() => { setHeaderOpenState(readHeaderOpen(headerKey)); }, [headerKey]);
  const setHeaderOpen = (open: boolean) => {
    setHeaderOpenState(open);
    try { localStorage.setItem(headerKey, open ? '1' : '0'); } catch { /* blocked: the toggle still works for this visit */ }
  };
  // Non-admins can't see Dispatch — a pasted ?tab=dispatch falls back to Estimate.
  const activeTab: LeadTab = rawTab === 'dispatch' && !isAdmin ? 'estimate' : rawTab;
  const setActiveTab = onChangeTab;
  const [inventoryOpen, setInventoryOpen] = useState(false);
  const [confirmEstimateSend, setConfirmEstimateSend] = useState(false);
  // The phone header's Email button: a request the Sales feed opens a compose for.
  const [composeReq, setComposeReq] = useState<number | null>(null);
  const [emailingEstimate, setEmailingEstimate] = useState(false);
  const [emailStatus, setEmailStatus] = useState<{ ok: boolean; text: string; onOverride?: () => void } | null>(null);
  const [bookOpen, setBookOpen] = useState(false);
  const [bookSendEmail, setBookSendEmail] = useState(true); // checkbox — checked by default
  const [bookSendClient, setBookSendClient] = useState(true); // the client's "Your Move is Booked" — checked by default
  const [booking, setBooking] = useState(false);
  // No Booked without a deposit: an admin may override with a reason (server-enforced).
  const [bookReason, setBookReason] = useState('');
  const [bookErr, setBookErr] = useState<{ code?: string; text: string } | null>(null);
  const [auditOpen, setAuditOpen] = useState(false); // Audit Report (send history) modal
  // Before the `if (!lead) return` below — hooks must run on every render.
  const sendGuard = useAction(); // client sends: one at a time, keyed
  const bookGuard = useAction(); // booking: one at a time, keyed

  // Dismiss the Audit Report modal on Escape (outside-click handled on the overlay).
  useEffect(() => {
    if (!auditOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setAuditOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [auditOpen]);
  const [portalBusy, setPortalBusy] = useState(false);
  const [editContactOpen, setEditContactOpen] = useState(false);
  // Bumped after an estimate email sends so the Activity Log refetches its note.
  const [activityRefresh, setActivityRefresh] = useState(0);

  // (Tab is URL-owned by App now; opening a different lead resets it there, and a
  // pasted ?tab= is honoured — so no local reset effect that would clobber it.)

  const { data: leadData, loading, refresh } = useApi<any>(async () => (await api.getLead(leadId)).data, {
    dependencies: [leadId],
  });

  /**
   * NO SAVE EVER BLANKS THE LEAD FILE. Every field edit used to call refetch(),
   * which sets `loading` — and the page answers loading with "Loading lead…" in
   * place of the whole file, so each blur flashed it away and back. Now:
   *
   *   - the edited fields are laid over the lead at once (leadOverlay), so the
   *     page shows them before the server answers;
   *   - the write is followed by a QUIET refresh (useApi.refresh), which keeps
   *     the current lead on screen until the new one replaces it;
   *   - only then is the overlay for those fields dropped — the refreshed lead
   *     carries them, plus anything the server derived from them.
   *
   * `loading` is therefore only ever true on first load and on opening another
   * lead, which is the one time "Loading lead…" is the truth.
   */
  const [leadOverlay, setLeadOverlay] = useState<Record<string, any>>({});
  const lead = useMemo(() => (leadData ? { ...leadData, ...leadOverlay } : leadData), [leadData, leadOverlay]);
  const dropOverlay = (patch: Record<string, any>) =>
    setLeadOverlay((o) => {
      const next = { ...o };
      for (const k of Object.keys(patch)) if (next[k] === patch[k]) delete next[k];   // a newer edit to the same field stays
      return next;
    });
  /** Re-read the lead without blanking the page. Panels call this after changing it. */
  const refreshLead = useCallback(async () => {
    try { await refresh(); } catch (e) { console.error('Lead refresh failed:', e); }
  }, [refresh]);
  const { data: tariffsData } = useApi<any[]>(async () => (await api.getTariffs()).data || []);
  // The Declared Value Protection rates live on the company row (migration 034),
  // the same place the card-fee rate does. GET /companies is company-scoped and
  // not admin-gated, so a rep reads it too — they are the ones quoting.
  const { data: companyArr } = useApi<any[]>(async () => (await api.getCompany()).data || []);
  const { data: branchesData } = useApi<any[]>(async () => (await api.getBranches()).data || []);
  const { data: usersData } = useApi<any[]>(async () => (await api.getUsers()).data || []);
  const { data: sourceRows } = useApi<any[]>(async () => {
    try {
      return (await api.getConfigList('lead_source')).data || [];
    } catch {
      return [];
    }
  });
  const { data: arrivalWindowRows } = useApi<any[]>(async () => {
    try {
      return (await api.getConfigList('arrival_window')).data || [];
    } catch {
      return [];
    }
  });

  // Only a lead we are not showing yet blanks the page. A refetch of THIS lead
  // (any cause) keeps the file — and whatever is being typed in it — on screen.
  const showingThisLead = !!leadData && String(leadData.id) === String(leadId);

  // The browser tab and the Back-button history name the file (App sets the
  // other pages' titles and leaves this page's to here). Follows a name edit.
  const titleName = lead ? `${lead.first_name ?? ''} ${lead.last_name ?? ''}`.trim() : '';
  const titleJob = lead ? jobNo(lead) : null;
  useEffect(() => {
    if (!showingThisLead) { document.title = 'Lead – BVL CRM'; return; }
    document.title = `${titleName || 'Lead'}${titleJob ? ` #${titleJob}` : ''} – BVL CRM`;
  }, [showingThisLead, titleName, titleJob]);

  if (loading && !showingThisLead) return <div style={{ padding: '40px', color: 'var(--crm-text)' }}>Loading lead…</div>;
  if (!lead) return <div style={{ padding: '40px', color: 'var(--crm-text)' }}>Lead not found</div>;

  const tariff = tariffsData?.find((t) => t.id === lead.tariff_id);
  const isBinding = lead.is_binding ?? lead.binding ?? false;
  const jobNumber = formatJobNo(lead);  // already '#'-prefixed

  const leadSources = sourceRows && sourceRows.length > 0 ? sourceRows.map((r) => r.value) : FALLBACK_SOURCES;
  const arrivalWindows =
    arrivalWindowRows && arrivalWindowRows.length > 0 ? arrivalWindowRows.map((r) => r.value) : FALLBACK_ARRIVAL_WINDOWS;

  const handleUpdateLead = async (patch: Record<string, any>) => {
    setLeadOverlay((o) => ({ ...o, ...patch }));
    try {
      await api.updateLead(leadId, patch);
      await refreshLead();
    } catch (err) {
      // Not saved: take the edit back off the screen rather than show a value
      // the server does not hold. (The error handling is otherwise unchanged.)
      console.error('Failed to update lead:', err);
    } finally {
      dropOverlay(patch);
    }
  };

  /**
   * Save for a long text field (NotesCard / useDraftText). Unlike
   * handleUpdateLead it THROWS on failure — the card keeps the text and says
   * "Not saved" — and lays nothing over the lead: the card shows its own draft.
   * `firstOfSession` asks the server for the one activity entry per session.
   */
  const saveLeadText = (field: string) => async (v: string, firstOfSession: boolean) => {
    await api.updateLead(leadId, { [field]: v }, { editSession: firstOfSession });
    void refreshLead();
  };
  const notesProps = (field: 'customer_notes' | 'contractor_notes' | 'internal_notes') => ({
    value: lead[field],
    storageKey: draftKey(leadId, field),
    updatedAt: leadData?.updated_at ?? null,
    onSave: saveLeadText(field),
  });

  // Run a client send with INLINE feedback (never an alert). A suppression block
  // (409 SUPPRESSED — the address bounced before) surfaces the reason + a "Send
  // anyway" action that retries with override. Refreshes the activity log.
  // One client send at a time (useAction), keyed so a repeat is not a second email.
  const runSend = (fn: (override: boolean, key: string) => Promise<any>, okText: string) =>
    sendGuard.run((key) => runSendNow(fn, okText, key));
  const runSendNow = async (fn: (override: boolean, key: string) => Promise<any>, okText: string, key: string) => {
    setEmailingEstimate(true);
    setEmailStatus(null);
    try {
      await fn(false, key);
      setActivityRefresh((n) => n + 1);
      setEmailStatus({ ok: true, text: okText });
    } catch (err: any) {
      if (err?.code === 'SUPPRESSED') {
        setEmailStatus({
          ok: false,
          text: err.message,
          onOverride: () => sendGuard.run(async (key2) => {
            setEmailStatus(null);
            setEmailingEstimate(true);
            try {
              await fn(true, key2);
              setActivityRefresh((n) => n + 1);
              setEmailStatus({ ok: true, text: okText });
            } catch (e2: any) {
              setEmailStatus({ ok: false, text: e2?.message || 'Send failed — please try again.' });
            } finally {
              setEmailingEstimate(false);
            }
          }),
        });
      } else {
        setEmailStatus({ ok: false, text: err?.message || 'Send failed — please try again.' });
      }
    } finally {
      setEmailingEstimate(false);
    }
  };

  // Email the customer their estimate, through the shared send layer — after
  // the confirm dialog (EstimateSendConfirm: who, total, lines, warnings).
  const handleEmailEstimate = async () => {
    const to = (lead.email || '').trim();
    if (!to) {
      setEmailStatus({ ok: false, text: 'No customer email on file. Add an email to this lead first.' });
      return;
    }
    setConfirmEstimateSend(true);
  };
  const sendEstimateConfirmed = async () => {
    setConfirmEstimateSend(false);
    const to = (lead.email || '').trim();
    await runSend((override, key) => api.withIdempotencyKey(key).emailEstimate(leadId, override), `Estimate sent to ${to}.`);
  };

  // Generate/fetch the customer portal link and open it in a new tab, copying it
  // to the clipboard so staff can also paste it. Lets us test the portal now,
  // before P3 embeds the link in the customer email.
  const handlePreviewPortal = async () => {
    setPortalBusy(true);
    try {
      const res = await api.createPortalLink(leadId);
      const url = res.data?.url;
      if (!url) throw new Error('No link returned');

      /*
       * TWO URLS, DELIBERATELY, and they must not be swapped.
       *
       * The CLIPBOARD gets the clean link — the one a rep pastes to a client.
       *
       * The TAB gets ?preview=1: the normal interactive portal, exactly as the
       * client sees it, with Accept disabled (a rep previewing must never
       * accept on the client's behalf — that was once live in production) and
       * the read marked as a preview so it never counts as the client opening
       * it. It used to get ?print=1, which is the PDF layout, not the portal.
       */
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        /* clipboard may be blocked — the new tab still opens */
      }
      const previewUrl = `${url}${url.includes('?') ? '&' : '?'}preview=1`;
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setEmailStatus({ ok: false, text: err?.message || 'Could not create portal link.' });
    } finally {
      setPortalBusy(false);
    }
  };

  // E3 Item 4a — ask the client to submit their inventory (portal placeholder;
  // submission UX is P5). Exact-send, inline status.
  /**
   * D2 — the SERVER PDF of the customer estimate portal, not a browser print of
   * this dark CRM page (which is what this did, and it was never a usable
   * artifact). Mints the portal link first, so the rep downloads exactly what
   * the client sees.
   */
  const handleDownloadEstimatePdf = async () => {
    try {
      const res: any = await api.createPortalLink(leadId);
      const token = res?.data?.token;
      if (!token) throw new Error('Could not prepare the estimate link.');
      const r = await fetch(`${API_BASE_URL}/portal/estimate/${encodeURIComponent(token)}/pdf`);
      if (!r.ok) throw new Error('Could not generate the estimate PDF.');
      const blob = await r.blob();
      const name = (r.headers.get('Content-Disposition') || '').match(/filename="?([^";]+)"?/)?.[1] || 'Estimate.pdf';
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = u; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 1000);
    } catch (e: any) {
      window.alert(e?.message || 'Could not generate the estimate PDF.');
    }
  };

  const handleRequestInventory = async () => {
    const to = (lead.email || '').trim();
    if (!to) {
      setEmailStatus({ ok: false, text: 'No customer email on file. Add an email to this lead first.' });
      return;
    }
    await runSend((override, key) => api.withIdempotencyKey(key).sendInventoryRequest(leadId, override), `Inventory request sent to ${to}.`);
  };

  // E3 Item 4c — EVERY path to Booked routes through the one confirmation dialog
  // (the stage dropdown included), so a booking is never silent: no stage change to
  // Booked happens without the notification + email choice. Other stages update directly.
  const handleStageChange = (newStage: string) => {
    if (newStage === 'Booked' && lead.stage !== 'Booked') {
      setBookSendEmail(true);
      setBookSendClient(true);
      setBookReason('');
      setBookErr(null);
      setBookOpen(true);
      return; // the dialog's confirm performs the booking (stage + notification + email)
    }
    handleUpdateLead({ stage: newStage });
  };

  // The dialog (below) gates whether the confirmation email goes out; the send
  // itself is exact-send.
  const doBook = () => bookGuard.run((key) => doBookNow(key));
  const doBookNow = async (key: string) => {
    setBooking(true);
    setEmailStatus(null);
    setBookErr(null);
    try {
      const res = await api.withIdempotencyKey(key).bookLead(leadId, bookSendEmail, false, noDeposit && isAdmin ? bookReason.trim() : undefined, { sendClient: bookSendClient });
      await refreshLead();
      setActivityRefresh((n) => n + 1);
      setBookOpen(false);
      const d = res.data;
      // Each email reports for itself. A suppression block offers "Send anyway",
      // which re-sends ONLY that email (only=…: the lead is already Booked, so
      // nothing is re-booked or re-notified).
      const resendOne = (which: 'team' | 'client') => () => bookGuard.run(async (key2) => {
        setEmailStatus(null);
        try {
          const r2 = await api.withIdempotencyKey(key2).bookLead(leadId, which === 'team', true, undefined, { sendClient: which === 'client', only: which });
          setActivityRefresh((n) => n + 1);
          const err2 = which === 'team' ? r2.data?.emailError : r2.data?.clientEmailError;
          setEmailStatus(err2 ? { ok: false, text: err2 } : { ok: true, text: which === 'team' ? 'Booking confirmation emailed to the team.' : 'Booking confirmation emailed to the client.' });
        } catch (e2: any) {
          setEmailStatus({ ok: false, text: e2?.message || 'Send failed — please try again.' });
        }
      });
      if (d?.emailError || d?.clientEmailError) {
        const blocked = d.clientEmailError && d.clientEmailBlocked ? 'client' : d.emailError && d.emailBlocked ? 'team' : null;
        setEmailStatus({
          ok: false,
          text: ['Booked.', d.emailError ? `Team email: ${d.emailError}` : '', d.clientEmailError ? `Client email: ${d.clientEmailError}` : ''].filter(Boolean).join(' '),
          onOverride: blocked ? resendOne(blocked) : undefined,
        });
      } else {
        const sent = [d?.emailed ? 'the team' : '', d?.clientEmailed ? 'the client' : ''].filter(Boolean);
        setEmailStatus({ ok: true, text: sent.length ? `Booked. Confirmation emailed to ${sent.join(' and ')}.` : 'Booked.' });
      }
    } catch (err: any) {
      // Shown in the dialog, which stays open: a deposit refusal is something to
      // read (and, for an admin, to answer with a reason), not a toast behind it.
      setBookErr({ code: err?.code, text: err?.message || 'Could not book this lead — please try again.' });
    } finally {
      setBooking(false);
    }
  };
  // The deposit check, as the lead payload reports it. The server decides; this
  // only shapes the dialog (and a refusal from the server shapes it too).
  const noDeposit = lead?.has_client_payment === false || bookErr?.code === 'DEPOSIT_REQUIRED' || bookErr?.code === 'OVERRIDE_REASON_REQUIRED';
  const reasonOk = bookReason.trim().length >= 3;
  const canConfirmBook = !noDeposit || (isAdmin && reasonOk);

  const repName = (id: string | null | undefined) => {
    const u = usersData?.find((x) => x.id === id);
    return u ? `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() : null;
  };

  // ---- Move-window range strings (for the job context strip + section) ----
  const longDistance = isLongDistance(lead);
  // Declared Value Protection. One call, one place — the same pure function the
  // contract and the invoice line use, so the rep is quoting the number the
  // client will see rather than an independently computed lookalike.
  const dvpCompany = companyArr?.[0] ?? {};
  const dvp = dvpQuote(lead, dvpCompany);

  // ---- Shared section renderers (used across all three tabs) --------------

  // Three equal columns: pricing on the first row, shipment on the second.
  // Move size and lead source live in the header controls, the move date is
  // the header date itself, and Binding is a switch in the section heading.
  const renderMoveDetailsSection = () => (
    <Section
      label="Move Details"
      action={<ToggleSwitch label="Binding" value={!!isBinding} onChange={(v) => handleUpdateLead({ is_binding: v })} />}
    >
      <div style={{ display: 'grid', gridTemplateColumns: COLS_3, gap: '9px' }}>
        <SelectField label="Move Type" value={lead.move_type} placeholder="— Select —" options={MOVE_TYPES.map((v) => ({ value: v, label: v }))} onChange={(v) => handleUpdateLead({ move_type: v || null })} />
        <SelectField label="Tariff (pricing)" value={lead.tariff_id} placeholder="— Select —" options={(tariffsData || []).map((t) => ({ value: t.id, label: `${t.name} ($${Number(t.base_rate ?? 0)}${t.pricing_method === 'weight' ? '/lb' : t.pricing_method === 'cuft' ? '/cuft' : ''})` }))} onChange={(v) => handleUpdateLead({ tariff_id: v || null })} />
        <SelectField label="Branch (tax)" value={lead.branch_id} placeholder="— Select —" options={(branchesData || []).map((b) => ({ value: b.id, label: `${b.name} (${Number(b.tax_rate ?? 0)}%)` }))} onChange={(v) => handleUpdateLead({ branch_id: v || null })} />
        {/* MW: entered weight snaps UP to the minimum — stored = shown = billed.
            The minimum itself is edited in the hint under Weight. */}
        <QuickEditField
          label="Weight"
          value={lead.weight_lbs ?? 0}
          numeric
          suffix="lbs"
          hint={
            <>
              <MinWeightHint
                value={Number(lead.minimum_weight ?? 2000)}
                onCommit={(mw) => {
                  // MW: raising the minimum above the entered weight snaps the weight up too.
                  const patch: Record<string, any> = { minimum_weight: mw };
                  if (mw > Number(lead.weight_lbs || 0)) patch.weight_lbs = mw;
                  // MW2 mirror: contractor minimum follows the client until overridden.
                  if (!lead.contractor_min_overridden) patch.contractor_minimum_weight = mw;
                  handleUpdateLead(patch);
                }}
              />
              {Number(lead.weight_lbs || 0) <= Number(lead.minimum_weight ?? 2000) && ' · at minimum'}
            </>
          }
          onCommit={(v) => handleUpdateLead({ weight_lbs: Math.max(parseInt(v) || 0, Number(lead.minimum_weight ?? 2000)) })}
        />
        <QuickEditField label="CuFT" value={lead.cubic_feet ?? 0} numeric suffix="cuft" onCommit={(v) => handleUpdateLead({ cubic_feet: parseInt(v) || 0 })} />
        {/* DVP is LONG DISTANCE ONLY. On a local move the field is not rendered
            at all rather than disabled: local carries Enhanced Coverage at
            $0.60/lb and the option is not sold, so an inert box would only
            invite the question. The value clamps UP to the minimum on commit,
            exactly as Weight does above — editable upward, never below. */}
        {dvpOffered(lead) && (
          <QuickEditField
            label="Declared Value"
            value={lead.declared_value ?? dvp.minimum}
            numeric
            prefix="$"
            hint={
              dvp.unset
                ? `min ${dvpMoney(dvp.minimum)} — not set`
                : `premium ${dvpMoney(dvp.premium)}${Number(lead.declared_value) <= dvp.minimum ? ' · at minimum' : ''}`
            }
            onCommit={(v) => handleUpdateLead({ declared_value: Math.max(parseFloat(v) || 0, dvp.minimum) })}
          />
        )}
      </div>
    </Section>
  );

  // Pickup and delivery side by side: each card is its address, its window
  // (long distance only) and its access fields, so the two ends of the move
  // read across from each other.
  const renderPickupDeliverySection = () => {
    const windowOpts = arrivalWindows.map((w) => ({ value: w, label: w }));
    return (
      <Section label="Pickup & Delivery">
        {/* Long-distance moves carry pickup/drop-off windows; local moves don't. */}
        {!longDistance && (
          <div style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '9px', padding: '12px', fontSize: '12px', color: 'var(--crm-dim)', marginBottom: '11px' }}>
            Local move — no long-distance windows.
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))', gap: '11px' }}>
          <AddressCard
            title="Pickup · Origin"
            leg="origin"
            lead={lead}
            stairs={lead.origin_stairs}
            walk={lead.origin_walk_ft}
            elevator={lead.origin_elevator}
            shuttle={lead.origin_shuttle}
            onSaveAddress={(patch) => handleUpdateLead(patch)}
            onStairs={(v) => handleUpdateLead({ origin_stairs: parseInt(v) || 0 })}
            onWalk={(v) => handleUpdateLead({ origin_walk_ft: parseInt(v) || 0 })}
            onElevator={(v) => handleUpdateLead({ origin_elevator: v })}
            onShuttle={(v) => handleUpdateLead({ origin_shuttle: v })}
            windows={longDistance && (
              <>
                <QuickEditField label="Pickup start" value={lead.pickup_start} type="date" onCommit={(v) => handleUpdateLead({ pickup_start: v || null })} />
                <QuickEditField label="Pickup end" value={lead.pickup_end} type="date" onCommit={(v) => handleUpdateLead({ pickup_end: v || null })} />
                <SelectField label="Arrival window" value={lead.pickup_arrival_window} placeholder="—" options={windowOpts} onChange={(v) => handleUpdateLead({ pickup_arrival_window: v || null })} />
              </>
            )}
          />
          <AddressCard
            title="Delivery · Destination"
            leg="destination"
            lead={lead}
            stairs={lead.destination_stairs}
            walk={lead.destination_walk_ft}
            elevator={lead.destination_elevator}
            shuttle={lead.destination_shuttle}
            onSaveAddress={(patch) => handleUpdateLead(patch)}
            onStairs={(v) => handleUpdateLead({ destination_stairs: parseInt(v) || 0 })}
            onWalk={(v) => handleUpdateLead({ destination_walk_ft: parseInt(v) || 0 })}
            onElevator={(v) => handleUpdateLead({ destination_elevator: v })}
            onShuttle={(v) => handleUpdateLead({ destination_shuttle: v })}
            windows={longDistance && (
              <>
                <QuickEditField label="Drop-off start" value={lead.dropoff_start} type="date" onCommit={(v) => handleUpdateLead({ dropoff_start: v || null })} />
                <QuickEditField label="Drop-off end" value={lead.dropoff_end} type="date" onCommit={(v) => handleUpdateLead({ dropoff_end: v || null })} />
                <SelectField label="Arrival window" value={lead.dropoff_arrival_window} placeholder="—" options={windowOpts} onChange={(v) => handleUpdateLead({ dropoff_arrival_window: v || null })} />
              </>
            )}
          />
        </div>
      </Section>
    );
  };

  const renderNotesSection = () => (
    <Section label="Notes">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '11px' }}>
        <NotesCard label="Customer notes" {...notesProps('customer_notes')} />
        <NotesCard label="Contractor notes" {...notesProps('contractor_notes')} />
      </div>
    </Section>
  );

  const renderActivitySection = () => (
    <Section label="Activity Log & Notes">
      {/* 073: the post-move feedback request, once the job has one. */}
      <FeedbackLine leadId={leadId} refreshKey={`${lead.stage}|${activityRefresh}`} />
      <ActivityLog leadId={leadId} users={usersData ?? undefined} refreshSignal={activityRefresh} leadEmail={lead.email} leadPhone={lead.phone} focusMessage={focusMessage ?? null} composeRequest={composeReq} onComposeHandled={() => setComposeReq(null)} />
    </Section>
  );


  const tabBtn = (tab: LeadTab, label: string) => {
    const active = activeTab === tab;
    return (
      <button
        key={tab}
        onClick={() => setActiveTab(tab)}
        style={{
          padding: '9px 18px',
          border: 'none',
          borderBottom: active ? '2px solid var(--crm-tab-active)' : '2px solid transparent',
          background: active ? 'var(--crm-panel-2)' : 'var(--crm-panel-2)',
          color: active ? 'var(--crm-text)' : 'var(--crm-muted)',
          cursor: 'pointer',
          fontSize: '12px',
          fontWeight: 600,
          borderRadius: '7px 7px 0 0',
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* ONE scroll container; the header inside is sticky */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {/* ===== Sticky header ===== */}
        <div style={{ position: 'sticky', top: 0, zIndex: 10, background: 'var(--crm-panel)', borderBottom: '1px solid var(--crm-line)', padding: phone ? '8px 12px 0' : '14px 16px 0' }}>
          {phone && !headerOpen ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', minWidth: 0 }}>
              <button onClick={onBack} aria-label="Back to customers" style={{ ...hdrBtn(), padding: '4px 9px' }}>←</button>
              <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', gap: '7px' }}>
                <span style={{ fontSize: '15px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                  {`${lead.first_name ?? ''} ${lead.last_name ?? ''}`.trim() || '—'}
                </span>
                <span style={{ fontSize: '11px', color: 'var(--crm-dim)', fontFamily: "'DM Mono', monospace", whiteSpace: 'nowrap' }}>{jobNumber}</span>
              </div>
              <span style={{ fontSize: '11px', fontWeight: 600, color: stageColor(lead.stage), background: stageBg(lead.stage), border: `1px solid ${tintOf(stageColor(lead.stage), 40)}`, borderRadius: '6px', padding: '3px 7px', whiteSpace: 'nowrap' }}>
                {lead.stage}
              </span>
              {/* Call and Email, one tap from the compact header. Calls start
                  only here: through the RingCentral widget on desktop, the
                  RingCentral app on mobile (company number), else tel: (E.164). Email opens the CRM's own compose on the
                  Sales tab, beside the Text box (RingCentral). */}
              {String(lead.phone ?? '').trim() && (
                <a href={telHref(lead.phone)}
                   onClick={(e) => {
                     if (ringCentralPhoneEnabled) { e.preventDefault(); startCall(lead.phone, lead.id); }
                     else if (ringCentralAppCalls) { e.preventDefault(); callWithRingCentralApp(lead.phone); }
                   }}
                   aria-label={`Call ${formatPhone(lead.phone)}`} title={`Call ${formatPhone(lead.phone)}`}
                   style={{ ...hdrBtn(), width: '44px', height: '44px', padding: 0, justifyContent: 'center', color: 'var(--crm-green)', textDecoration: 'none', flexShrink: 0 }}>
                  <Icons.phone size={18} aria-hidden="true" />
                </a>
              )}
              {String(lead.email ?? '').trim() && (
                <button onClick={() => { setActiveTab('sales'); setComposeReq(Date.now()); }} aria-label="Email this client" title="Email this client"
                        style={{ ...hdrBtn(), width: '44px', height: '44px', padding: 0, justifyContent: 'center', flexShrink: 0 }}>
                  <Icons.email size={18} aria-hidden="true" />
                </button>
              )}
              <button onClick={() => setHeaderOpen(true)} aria-expanded={false} aria-label="Show lead details" title="Show details" style={{ ...hdrBtn(), padding: '4px 9px' }}>▾</button>
            </div>
          ) : (<>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '8px' }}>
            <button
              onClick={onBack}
              style={hdrBtn()}
            >
              ← Back to customers
            </button>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setAuditOpen(true)}
                style={hdrBtn()}
              >
                <Icons.audit size={14} /> Audit Report
              </button>
              {phone && (
                <button onClick={() => setHeaderOpen(false)} aria-expanded={true} aria-label="Hide lead details" title="Hide details" style={{ ...hdrBtn(), padding: '4px 9px' }}>▴</button>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '13px', alignItems: 'flex-start' }}>
            <div style={{ width: '46px', height: '46px', borderRadius: '10px', background: 'var(--crm-panel-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', fontWeight: 700, color: 'var(--crm-muted)' }}>
              {getInitials(lead.first_name, lead.last_name)}
            </div>

            <div style={{ flex: 1 }}>
              <h1 style={{ fontSize: '19px', fontWeight: 700, marginBottom: '4px' }}>
                {lead.first_name} {lead.last_name}
                <span style={{ fontSize: '11px', background: 'var(--crm-panel-2)', color: 'var(--crm-dim)', padding: '2px 8px', borderRadius: '4px', marginLeft: '10px', fontFamily: "'DM Mono', monospace" }}>
                  {jobNumber}
                </span>
                {isBinding && <span style={{ fontSize: '10px', background: 'var(--crm-green-tint)', color: 'var(--crm-green)', padding: '2px 8px', borderRadius: '4px', marginLeft: '8px' }}>BINDING</span>}
              </h1>

              <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '9px', fontSize: '12px', color: 'var(--crm-muted)', alignItems: 'center' }}>
                <div style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}><Icons.phone size={13} /> <PhoneLink phone={lead.phone} leadId={lead.id} />{(lead as any).text_opted_out && <OptedOutBadge />}</div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}><Icons.email size={13} /> {lead.email || '—'}</div>
                <button
                  onClick={() => setEditContactOpen(true)}
                  style={{ ...hdrBtn(), padding: '2px 9px', fontSize: '11px', background: 'transparent' }}
                >
                  <Icons.edit size={12} /> Edit
                </button>
              </div>

              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                <select
                  value={lead.stage}
                  onChange={(e) => handleStageChange(e.target.value)}
                  // Closed is an admin's call, both ways (the server refuses reps too).
                  className="crm-stage-select"
                  disabled={!isAdmin && lead.stage === 'Closed'}
                  title={!isAdmin && lead.stage === 'Closed' ? 'Only an admin can move a job out of Closed' : undefined}
                  /* THE STAGE IS SHOWN ONCE NOW. It was a plain select here AND a
                     coloured chip in the right-hand column — two renderings of one
                     fact, and only one of them carried the colour that makes a
                     stage scannable. The select wears the colour; the chip is gone. */
                  style={{
                    padding: '5px 9px', background: stageBg(lead.stage), borderRadius: '6px', fontSize: '12px',
                    cursor: 'pointer', fontWeight: 600,
                    border: `1px solid ${tintOf(stageColor(lead.stage), 40)}`,
                    color: stageColor(lead.stage),
                  }}
                >
                  {STAGES.map((s) => (
                    <option key={s} value={s} disabled={!isAdmin && s === 'Closed' && lead.stage !== 'Closed'}>{s}</option>
                  ))}
                </select>
                {/* 048: a card payment with no Square cost — enter it on the
                    Finalize tab → Payments. */}
                {lead.square_cost_needed && (
                  <button
                    onClick={() => setActiveTab('finalize')}
                    title="A card payment on this job has no Square cost recorded. Enter it under Payments on the Finalize tab."
                    style={{ padding: '4px 9px', background: 'var(--crm-amber-tint)', border: '1px solid var(--crm-amber-soft-line)', color: 'var(--crm-amber)', borderRadius: '6px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
                  >
                    Square cost needed
                  </button>
                )}
                {/* Owner assignment is ADMIN-ONLY (N3): editing a file is not the
                    same as taking it. Reps still SEE the owner (read-only). */}
                {isAdmin ? (
                  <>
                    <select
                      value={lead.assigned_rep_id ?? ''}
                      onChange={(e) => handleUpdateLead({ assigned_rep_id: e.target.value || null })}
                      title="Sales rep"
                      style={{ padding: '5px 9px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', color: 'var(--crm-text)', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      <option value="">Unassigned</option>
                      {(usersData || []).map((u) => (
                        <option key={u.id} value={u.id}>{`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim()}</option>
                      ))}
                    </select>
                    <select
                      value={lead.cs_rep_id ?? ''}
                      onChange={(e) => handleUpdateLead({ cs_rep_id: e.target.value || null })}
                      title="Customer service rep"
                      style={{ padding: '5px 9px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', color: 'var(--crm-text)', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      <option value="">CS: None</option>
                      {(usersData || []).map((u) => (
                        <option key={u.id} value={u.id}>{`CS: ${`${u.first_name ?? ''} ${u.last_name ?? ''}`.trim()}`}</option>
                      ))}
                    </select>
                  </>
                ) : (
                  <span style={{ padding: '5px 9px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', color: 'var(--crm-muted)', borderRadius: '6px', fontSize: '12px' }}>
                    Rep: {repName(lead.assigned_rep_id) ?? 'Unassigned'}{lead.cs_rep_id ? ` · CS: ${repName(lead.cs_rep_id) ?? '—'}` : ''}
                  </span>
                )}
                {/* Move size and lead source sit with the other file-level
                    controls. Editable by everyone, as they were in Move details. */}
                <select
                  value={lead.move_size ?? ''}
                  onChange={(e) => handleUpdateLead({ move_size: e.target.value || null })}
                  title="Move size"
                  style={{ padding: '5px 9px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', color: 'var(--crm-text)', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                >
                  <option value="">Size: —</option>
                  {/* move_size is free text: a provider's label ("5+ Bedroom")
                      or an imported value is not on the list, and a select
                      with no matching option shows "Size: —" as if empty. */}
                  {lead.move_size && !MOVE_SIZES.includes(lead.move_size) && (
                    <option value={lead.move_size}>{lead.move_size}</option>
                  )}
                  {MOVE_SIZES.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
                <select
                  value={lead.lead_source ?? ''}
                  onChange={(e) => handleUpdateLead({ lead_source: e.target.value || null })}
                  title="Lead source"
                  style={{ padding: '5px 9px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', color: 'var(--crm-text)', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                >
                  <option value="">Source: —</option>
                  {/* A source set by intake or the New Lead pop-up (Settings →
                      Lead sources) may not be on the options list; show it
                      rather than "Source: —". */}
                  {lead.lead_source && !leadSources.includes(lead.lead_source) && (
                    <option value={lead.lead_source}>{`Source: ${lead.lead_source}`}</option>
                  )}
                  {leadSources.map((v) => (
                    <option key={v} value={v}>{`Source: ${v}`}</option>
                  ))}
                </select>
                {/* Three things you do with the estimate, and two with the
                    inventory. See HeaderMenu for why these are no longer five
                    buttons across the top of every file. */}
                <HeaderMenu
                  label="Estimate"
                  items={[
                    { label: 'Download estimate PDF', onClick: handleDownloadEstimatePdf, title: 'The server PDF of the customer estimate portal — exactly what the client sees' },
                    { label: emailingEstimate ? 'Sending…' : 'Email estimate', onClick: handleEmailEstimate, disabled: emailingEstimate },
                    { label: portalBusy ? 'Opening…' : 'Preview portal', onClick: handlePreviewPortal, disabled: portalBusy },
                  ]}
                />
                <HeaderMenu
                  label="Inventory"
                  items={[
                    { label: 'Inventory', onClick: () => setInventoryOpen(true) },
                    { label: 'Request inventory', onClick: handleRequestInventory, disabled: emailingEstimate },
                  ]}
                />
              </div>
              {emailStatus && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: emailStatus.ok ? 'var(--crm-green)' : 'var(--crm-red)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <span>{emailStatus.text}</span>
                  {emailStatus.onOverride && (
                    <button onClick={emailStatus.onOverride} style={{ padding: '3px 10px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-amber-line)', color: 'var(--crm-amber)', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}>Send anyway</button>
                  )}
                </div>
              )}
            </div>

            {/* WHAT IS NOT SAID ANYWHERE ELSE. This column held the stage (already a
                control on the left) and the rep (already in the Rep/CS pill), at
                three different sizes. Both were removed rather than restyled: the
                move date is the one thing here that appears nowhere else, and it
                is the thing a rep opens this file to check — and, since the
                Move date box left Move details, the one place it is edited. */}
            <HeaderMoveDate value={lead.move_date} onCommit={(v) => handleUpdateLead({ move_date: v || null })} />
          </div>
          </>)}

          {/* Tabs — kept when collapsed: they are how the file is moved around.
              On a phone they stay on one line and scroll sideways. */}
          <div style={{ display: 'flex', gap: '4px', marginTop: phone && !headerOpen ? 0 : '10px', ...(phone ? { overflowX: 'auto', flexWrap: 'nowrap', whiteSpace: 'nowrap' } : {}) }}>
            {tabBtn('estimate', 'Estimate')}
            {tabBtn('sales', 'Sales')}
            {isAdmin && tabBtn('dispatch', 'Dispatch')}
            {tabBtn('finalize', 'Finalize')}
          </div>
        </div>

        {/* ===== Coverage flag (all tabs) =====
            WHAT WAS HERE AND WHY IT WENT. A grey strip restated the move date,
            type, size, weight, cubic feet, both addresses and the pickup /
            drop-off windows — every one of which is already on the sticky header
            above (the date) or in the Move details, Addresses and Move windows
            sections of the tab directly below. Three renderings of one fact, and
            the strip was the one nobody could act on.

            THE DVP FLAG STAYS, because it is the only thing here that appears
            nowhere else: `dvp.elected` is set from the client's own election on
            the signed document, so it states an agreement rather than the
            quoted premium the Move details section shows. A lead merely quoted
            a declared value still shows nothing. */}
        {dvp.elected && (
          <div style={{ background: 'var(--crm-panel)', borderBottom: '1px solid var(--crm-line)', padding: '8px 16px' }}>
            <span
              title={`${DVP_NAME} ${lead.dvp_elected_by === 'office' ? 'elected by the office' : 'accepted by the client'} — ${dvpMoney(dvp.declaredValue)} declared, premium ${dvpMoney(dvp.premium)}${dvp.overridden ? ' (fixed)' : ' (provisional on the estimate weight)'}`}
              style={{ background: 'var(--crm-green-tint)', color: 'var(--crm-green)', border: '1px solid var(--crm-green-soft-line)', borderRadius: '4px', padding: '2px 8px', fontSize: '10px', fontWeight: 700, letterSpacing: '0.3px' }}
            >
              {DVP_NAME} {lead.dvp_elected_by === 'office' ? 'elected' : 'accepted'} · {dvpMoney(dvp.declaredValue)} declared · {dvpMoney(dvp.premium)}{dvp.overridden ? ' fixed' : ''}
            </span>
          </div>
        )}

        {/* ===== Tab content ===== */}
        <div style={{ padding: '16px' }}>
          {activeTab === 'estimate' && (
            <>
              {renderMoveDetailsSection()}
              {renderNotesSection()}
              {renderPickupDeliverySection()}
              {/* Staff only (051): never in a client or contractor payload. */}
              <Section label="Internal">
                <NotesCard label="Internal notes" {...notesProps('internal_notes')} placeholder="Call notes, inventory mentioned, access details…" rows={6} />
              </Section>
              <Section label="Estimate">
                <EstimatePanel leadId={leadId} lead={lead} tariff={tariff} onLeadChanged={refreshLead} />
                <BillingSections leadId={leadId} lead={lead} tariff={tariff} view="estimate" onGoFinalize={() => setActiveTab('finalize')} />
              </Section>
            </>
          )}

          {/* Phone: the activity feed first — notes, calls, emails and
              follow-ups are what a phone is used for here. */}
          {activeTab === 'sales' && (
            phone ? (
              <>
                {renderActivitySection()}
                {renderMoveDetailsSection()}
                {renderPickupDeliverySection()}
              </>
            ) : (
              <>
                {renderMoveDetailsSection()}
                {renderPickupDeliverySection()}
                {renderActivitySection()}
              </>
            )
          )}

          {/* Finalize (048): final weight / hours, the invoice draft, issue,
              payments and the invoice list. All users. */}
          {activeTab === 'finalize' && (
            <FinalizeTab leadId={leadId} lead={lead} tariff={tariff} onLeadChanged={refreshLead}
              onOpenDispatch={() => {
                setActiveTab('dispatch');
                // The tab loads its data first; then bring the contractor estimate into view.
                window.setTimeout(() => document.getElementById('contractor-estimate')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 600);
              }} />
          )}

          {activeTab === 'dispatch' && isAdmin && (
            <>
              {renderMoveDetailsSection()}
              {renderPickupDeliverySection()}
              {/* Contractor, job status, contractor notes and minimum weight
                  sit in pairs at the top of the tab (DispatchTab). */}
              <DispatchTab leadId={leadId} lead={lead} onLeadChanged={refreshLead} repName={repName} onEditNotes={() => setActiveTab('estimate')} />
            </>
          )}
        </div>
      </div>

      {confirmEstimateSend && (
        <EstimateSendConfirm leadId={leadId} onConfirm={() => void sendEstimateConfirmed()} onCancel={() => setConfirmEstimateSend(false)} />
      )}

      {inventoryOpen && (
        <InventoryPanel
          leadId={leadId}
          lead={lead}
          onClose={() => setInventoryOpen(false)}
          onApplied={refreshLead}
        />
      )}

      <EditContactModal
        isOpen={editContactOpen}
        onClose={() => setEditContactOpen(false)}
        lead={lead}
        onSaved={refreshLead}
      />

      {/* Audit Report — the send history, moved off the bottom of the page into a
          modal reachable from the sticky header. Escape or click-outside dismisses. */}
      {auditOpen && (
        <div
          onClick={() => setAuditOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '48px 16px', overflowY: 'auto' }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--crm-bg)', border: '1px solid var(--crm-line)', borderRadius: '12px', width: '640px', maxWidth: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '1px solid var(--crm-line)' }}>
              <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--crm-text)', display: 'inline-flex', alignItems: 'center', gap: '8px' }}><Icons.audit size={17} /> Audit Report</div>
              <button onClick={() => setAuditOpen(false)} aria-label="Close" style={{ background: 'none', border: 'none', color: 'var(--crm-muted)', cursor: 'pointer', display: 'inline-flex' }}><Icons.close size={18} /></button>
            </div>
            <div style={{ padding: '14px 16px' }}>
              <SendHistory leadId={leadId} users={usersData ?? []} isAdmin={isAdmin} refreshSignal={activityRefresh} />
            </div>
          </div>
        </div>
      )}

      {/* Book confirmation (E3 Item 4c) — the checkbox gates the email; booking
          itself is exact-send. Not a preview/draft of the email. */}
      {bookOpen && (
        <div
          onClick={() => !booking && setBookOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '12px', padding: '20px 22px', width: '380px', maxWidth: '90vw' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--crm-text)', marginBottom: '6px' }}>Move this client to Booked?</div>
            <div style={{ fontSize: '12px', color: 'var(--crm-muted)', marginBottom: '14px' }}>
              {`${lead.first_name ?? ''} ${lead.last_name ?? ''}`.trim() || 'This client'} will be marked Booked and the team notified in-app.
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--crm-text)', cursor: 'pointer', marginBottom: '10px' }}>
              <button
                type="button"
                onClick={() => setBookSendEmail((v) => !v)}
                aria-label={bookSendEmail ? 'Sending booking confirmation email' : 'Not sending email'}
                style={{ background: 'none', border: 'none', padding: 0, color: bookSendEmail ? 'var(--crm-green)' : 'var(--crm-dim)', cursor: 'pointer', display: 'inline-flex' }}
              >
                {bookSendEmail ? <Icons.checkboxOn size={18} /> : <Icons.checkboxOff size={18} />}
              </button>
              Send booking confirmation email to the team
            </label>
            {/* The client's "Your Move is Booked" (lib/bookingConfirmation.ts), from
                the rep, asking them to check the details on file. */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--crm-text)', cursor: 'pointer', marginBottom: '18px' }}>
              <button
                type="button"
                onClick={() => setBookSendClient((v) => !v)}
                aria-label={bookSendClient ? 'Emailing the booking confirmation to the client' : 'Not emailing the client'}
                style={{ background: 'none', border: 'none', padding: 0, color: bookSendClient ? 'var(--crm-green)' : 'var(--crm-dim)', cursor: 'pointer', display: 'inline-flex' }}
              >
                {bookSendClient ? <Icons.checkboxOn size={18} /> : <Icons.checkboxOff size={18} />}
              </button>
              <span>
                Email booking confirmation to client
                {!String(lead.email ?? '').trim() && <span style={{ color: 'var(--crm-amber)', fontSize: '11px' }}> — no email on file</span>}
              </span>
            </label>
            {/* NO BOOKED WITHOUT A DEPOSIT. Reps are stopped; an admin may book
                anyway with a reason, which goes on the activity log. */}
            {noDeposit && (
              <div style={{ background: 'var(--crm-amber-tint)', border: '1px solid var(--crm-amber-soft-line)', borderRadius: '8px', padding: '10px', marginBottom: '14px' }}>
                <div style={{ fontSize: '12px', color: 'var(--crm-amber)', fontWeight: 600, marginBottom: '4px' }}>No deposit recorded</div>
                <div style={{ fontSize: '12px', color: 'var(--crm-muted)' }}>
                  {isAdmin
                    ? 'A job is booked once its deposit is recorded (Finalize tab → Payments). To book it anyway, give a reason — it is logged to the activity log.'
                    : 'A job can be Booked only once a client payment (the deposit) is recorded. Record it under Payments on the Finalize tab first, or ask an admin.'}
                </div>
                {isAdmin && (
                  <textarea
                    value={bookReason}
                    onChange={(e) => setBookReason(e.target.value)}
                    placeholder="Reason for booking without a deposit"
                    rows={2}
                    style={{ width: '100%', boxSizing: 'border-box', marginTop: '8px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '6px', color: 'var(--crm-text)', fontSize: '12px', padding: '7px 9px', resize: 'vertical', fontFamily: 'inherit' }}
                  />
                )}
              </div>
            )}
            {bookErr && bookErr.code !== 'DEPOSIT_REQUIRED' && bookErr.code !== 'OVERRIDE_REASON_REQUIRED' && (
              <div style={{ fontSize: '12px', color: 'var(--crm-red)', marginBottom: '12px' }}>{bookErr.text}</div>
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setBookOpen(false)} disabled={booking} style={{ padding: '7px 14px', background: 'none', border: '1px solid var(--crm-line)', color: 'var(--crm-muted)', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
              <button onClick={doBook} disabled={booking || !canConfirmBook} style={{ padding: '7px 14px', background: noDeposit ? 'var(--crm-amber-fill)' : 'var(--crm-green-fill)', border: 'none', color: noDeposit ? 'var(--crm-on-amber)' : 'var(--crm-on-green)', borderRadius: '7px', cursor: booking || !canConfirmBook ? 'default' : 'pointer', fontSize: '12px', fontWeight: 700, opacity: booking || !canConfirmBook ? 0.5 : 1 }}>
                {booking ? 'Booking…' : noDeposit ? 'Book without deposit' : 'Confirm booking'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Small uppercase muted section label + body wrapper (matches the reference CRM).
// `action` sits at the right-hand end of the heading (e.g. the Binding switch).
function Section({ label, action, children }: { label: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '22px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', margin: '0 0 12px' }}>
        <h3 style={{ fontSize: '11px', fontWeight: 700, color: 'var(--crm-dim)', textTransform: 'uppercase', margin: 0 }}>{label}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

// Labeled dropdown card, styled to match QuickEditField.
function SelectField({
  label,
  value,
  options,
  onChange,
  placeholder,
}: {
  label: string;
  value: any;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <div style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '8px', padding: '10px' }}>
      <label style={{ fontSize: '9px', fontWeight: 600, color: 'var(--crm-dim)', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>{label}</label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--crm-text)', fontSize: '13px', outline: 'none', padding: 0, cursor: 'pointer' }}
      >
        {placeholder !== undefined && <option value="" style={{ background: 'var(--crm-panel-2)' }}>{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value} style={{ background: 'var(--crm-panel-2)' }}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// Multiline notes card. Autosaves through useDraftText: ~1.5s after typing
// stops and on blur. Unsaved text wins over any refetch, is backed up to this
// browser until it saves, and holds the tab open with the leave-page prompt.
function NotesCard({ label, value, storageKey, updatedAt, onSave, placeholder, rows }: {
  label: string; value: any; storageKey: string; updatedAt: string | null;
  onSave: (v: string, firstOfSession: boolean) => Promise<void>; placeholder?: string; rows?: number;
}) {
  const f = useDraftText({ storageKey, serverValue: String(value ?? ''), serverUpdatedAt: updatedAt, save: onSave });
  const at = (ms: number) => new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, color: 'var(--crm-accent)', cursor: 'pointer', fontSize: '11px' };
  return (
    <div style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '9px', padding: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', marginBottom: '8px' }}>
        <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--crm-muted)' }}>{label}</span>
        <SaveState status={f.status} error={f.error} onRetry={f.retry} />
      </div>
      {f.offer && (
        <div style={{ fontSize: '11px', color: 'var(--crm-amber)', marginBottom: '6px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <span>Unsaved text from {at(f.offer.at)} on this computer (the saved notes are newer).</span>
          <button type="button" onClick={f.restoreOffer} style={linkBtn}>Restore</button>
          <button type="button" onClick={f.discardOffer} style={{ ...linkBtn, color: 'var(--crm-muted)' }}>Discard</button>
        </div>
      )}
      <textarea
        value={f.value}
        placeholder={placeholder}
        rows={rows}
        onChange={(e) => f.onChange(e.target.value)}
        onBlur={f.onBlur}
        style={{ width: '100%', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '6px', padding: '8px', color: 'var(--crm-text)', fontSize: '12px', minHeight: rows ? undefined : '70px', resize: 'vertical', boxSizing: 'border-box' }}
      />
      {f.restoredAt != null && f.status !== 'saved' && (
        <div style={{ fontSize: '11px', color: 'var(--crm-muted)', marginTop: '4px' }}>Restored unsaved text from {at(f.restoredAt)}.</div>
      )}
    </div>
  );
}

// "Saving… / Saved / Not saved — retry", quiet, beside a field's label.
function SaveState({ status, error, onRetry }: { status: DraftStatus; error: string | null; onRetry: () => void }) {
  if (status === 'saving') return <span style={{ fontSize: '10px', color: 'var(--crm-muted)' }}>Saving…</span>;
  if (status === 'saved') return <span style={{ fontSize: '10px', color: 'var(--crm-dim)' }}>Saved</span>;
  if (status === 'error') {
    return (
      <button type="button" onClick={onRetry} title={error ?? undefined} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--crm-red)', cursor: 'pointer', fontSize: '10px', fontWeight: 600 }}>
        Not saved — retry
      </button>
    );
  }
  return null;
}

// Uncontrolled-ish field: edits locally, commits on blur so we don't PUT on every keystroke.
// `numeric` shows thousands separators until focused and the plain number while
// editing; what is committed is always the plain number. `prefix`/`suffix` are
// units drawn inside the box. A hint sits under the input, so labels line up.
function QuickEditField({ label, value, onCommit, type = 'text', hint, numeric, prefix, suffix }: {
  label: string; value: any; onCommit: (v: string) => void; type?: string; hint?: React.ReactNode;
  numeric?: boolean; prefix?: string; suffix?: string;
}) {
  const [draft, setDraft] = useState<string>(String(value ?? ''));
  const [focused, setFocused] = useState(false);
  const atFocus = React.useRef('');
  // A refetch never overwrites what is being typed: the server value is taken
  // only while the field is not focused (blur commits the draft first).
  React.useEffect(() => { if (!focused) setDraft(String(value ?? '')); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown =
    numeric && !focused && String(draft).trim() !== '' && Number.isFinite(Number(draft))
      ? Number(draft).toLocaleString('en-CA', { maximumFractionDigits: 2 })
      : draft;
  const unit: React.CSSProperties = { fontSize: '12px', color: 'var(--crm-dim)', flexShrink: 0 };
  return (
    <div style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '8px', padding: '10px' }}>
      <label style={{ fontSize: '9px', fontWeight: 600, color: 'var(--crm-dim)', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
        {prefix && <span style={unit}>{prefix}</span>}
        <input
          {...(!numeric && type === 'number' ? numberInput(1) : { type: numeric ? 'text' : type })}
          inputMode={numeric ? 'decimal' : undefined}
          value={shown}
          onChange={(e) => setDraft(numeric ? e.target.value.replace(/[^\d.]/g, '') : e.target.value)}
          onFocus={() => { atFocus.current = String(value ?? ''); setFocused(true); }}
          onBlur={() => {
            setFocused(false);
            // Edited: commit. Not edited: catch up with any server change made
            // while focused, rather than write the old value back over it.
            if (draft !== atFocus.current) onCommit(draft);
            else setDraft(String(value ?? ''));
          }}
          style={{ flex: 1, minWidth: 0, width: '100%', background: 'transparent', border: 'none', color: 'var(--crm-text)', fontSize: '13px', outline: 'none', padding: 0 }}
        />
        {suffix && <span style={unit}>{suffix}</span>}
      </div>
      {hint && <div style={{ marginTop: '5px', fontSize: '10px', color: 'var(--crm-amber)' }}>{hint}</div>}
    </div>
  );
}

/**
 * Up to three equal columns. Each track is at least a third of the row, so a
 * wide row never holds a fourth; below 180px a track the row drops to 2, then 1.
 */
const COLS_3 = 'repeat(auto-fill, minmax(max(180px, calc((100% - 18px) / 3)), 1fr))';

// Compact on/off switch with its label to the left (the Move details heading).
function ToggleSwitch({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      onClick={() => onChange(!value)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: value ? 'var(--crm-green)' : 'var(--crm-dim)' }}
    >
      {label}
      <span style={{ width: '28px', height: '16px', borderRadius: '8px', background: value ? 'var(--crm-green-fill)' : 'var(--crm-line)', position: 'relative', transition: 'background 0.15s' }}>
        <span style={{ position: 'absolute', top: '2px', left: value ? '14px' : '2px', width: '12px', height: '12px', borderRadius: '50%', background: 'var(--crm-white)', transition: 'left 0.15s' }} />
      </span>
    </button>
  );
}

/**
 * "min 2,000 lbs · edit" under Weight. Edit turns it into a small number input:
 * Enter or blur saves, Escape backs out. The caller owns what a new minimum does.
 */
function MinWeightHint({ value, onCommit }: { value: number; onCommit: (v: number) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const cancelled = React.useRef(false);
  if (!editing) {
    return (
      <span style={{ color: 'var(--crm-muted)' }}>
        min {value.toLocaleString('en-CA')} lbs ·{' '}
        <button
          type="button"
          onClick={() => { cancelled.current = false; setDraft(String(value)); setEditing(true); }}
          style={{ background: 'none', border: 'none', padding: 0, color: 'var(--crm-accent)', cursor: 'pointer', fontSize: 'inherit' }}
        >
          edit
        </button>
      </span>
    );
  }
  return (
    <span style={{ color: 'var(--crm-muted)' }}>
      min{' '}
      <input
        autoFocus
        inputMode="numeric"
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace(/[^\d]/g, ''))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          else if (e.key === 'Escape') { cancelled.current = true; e.currentTarget.blur(); }
        }}
        onBlur={() => {
          setEditing(false);
          const mw = parseInt(draft) || 0;
          if (!cancelled.current && mw !== value) onCommit(mw);
        }}
        style={{ width: '64px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-accent-line)', borderRadius: '4px', color: 'var(--crm-text)', fontSize: '11px', padding: '1px 4px' }}
      />{' '}
      lbs
    </span>
  );
}

/** Whole days from today in Toronto to a 'YYYY-MM-DD'. Calendar math only, so
 *  neither the browser's zone nor UTC can shift the day. */
function daysUntilToronto(day: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const utc = (d: string) => { const [y, m, dd] = d.split('-').map(Number); return Date.UTC(y, m - 1, dd); };
  return Math.round((utc(day) - utc(todayInTz(DEFAULT_TZ))) / 86400000);
}

/**
 * The header move date, and where it is edited. Click opens the native date
 * picker; the value is the 'YYYY-MM-DD' string the input gives, never a Date,
 * so the day saved is the day picked. Commits on blur or Enter (as the old
 * Move date box did); Escape backs out.
 */
function HeaderMoveDate({ value, onCommit }: { value: string | null | undefined; onCommit: (v: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [hover, setHover] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const cancelled = React.useRef(false);
  const typed = React.useRef(false);
  const day = value ? String(value).slice(0, 10) : '';
  const diff = daysUntilToronto(day);
  const color = diff === 0 ? 'var(--crm-amber)' : diff != null && diff < 0 ? 'var(--crm-red)' : 'var(--crm-muted)';
  const countdown =
    diff === null ? null : diff === 0 ? 'Today' : diff === 1 ? 'Tomorrow' : diff < 0 ? `${Math.abs(diff)}d overdue` : `In ${diff}d`;

  useEffect(() => {
    if (!editing) return;
    cancelled.current = false;
    typed.current = false;
    const el = inputRef.current;
    el?.focus();
    try { el?.showPicker?.(); } catch { /* not supported or no user gesture — the focused input still works */ }
  }, [editing]);

  const caption = (
    <div style={{ fontSize: '10px', color: 'var(--crm-dim)', textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 700 }}>Move date</div>
  );

  if (editing) {
    return (
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        {caption}
        <input
          ref={inputRef}
          type="date"
          defaultValue={day}
          // A day picked from the calendar saves at once. Typed digits form
          // valid-but-wrong dates on the way (0002-…, 0020-…), so typing waits
          // for blur or Enter instead.
          onChange={(e) => { if (!typed.current) e.currentTarget.blur(); }}
          onBlur={(e) => {
            setEditing(false);
            if (!cancelled.current && e.target.value !== day) onCommit(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            else if (e.key === 'Escape') { cancelled.current = true; e.currentTarget.blur(); }
            else if (e.key !== 'Tab') typed.current = true;
          }}
          style={{ marginTop: '2px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-accent-line)', borderRadius: '6px', color: 'var(--crm-text)', fontSize: '13px', padding: '3px 6px', colorScheme: 'dark' }}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title="Change move date"
      style={{ textAlign: 'right', flexShrink: 0, background: hover ? 'var(--crm-panel-2)' : 'transparent', border: 'none', borderRadius: '7px', padding: '2px 6px', margin: '-2px -6px', cursor: 'pointer', font: 'inherit' }}
    >
      {caption}
      <div style={{ fontSize: '15px', fontWeight: 700, color: day ? color : 'var(--crm-accent)', fontFamily: day ? "'DM Mono', monospace" : undefined, marginTop: '2px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px' }}>
        <span style={{ opacity: hover ? 1 : 0, color: 'var(--crm-muted)', display: 'inline-flex', transition: 'opacity 0.1s' }}><Icons.edit size={12} /></span>
        {day ? formatDate(day) : 'Set move date'}
      </div>
      {countdown && <div style={{ fontSize: '11px', color, marginTop: '1px' }}>{countdown}</div>}
    </button>
  );
}

interface AddressCardProps {
  title: string;
  leg: Leg;
  lead: any;
  stairs: number | null | undefined;
  walk: number | null | undefined;
  elevator: boolean | null | undefined;
  shuttle: boolean | null | undefined;
  /** The six address columns plus the one line built from them (migration 052). */
  onSaveAddress: (patch: Record<string, string | null>) => void;
  onStairs: (v: string) => void;
  onWalk: (v: string) => void;
  onElevator: (v: boolean) => void;
  onShuttle: (v: boolean) => void;
  /** The move window for this end (long distance only), between address and access. */
  windows?: React.ReactNode;
}

function AddressCard({ title, leg, lead, stairs, walk, elevator, shuttle, onSaveAddress, onStairs, onWalk, onElevator, onShuttle, windows }: AddressCardProps) {
  const [editing, setEditing] = useState(false);
  const line: string = lead?.[`${leg}_address`] ?? '';
  const check = needsCheck(lead, leg);
  const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '11px', fontWeight: 600 };
  return (
    <div style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '9px', padding: '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--crm-muted)' }}>{title}</div>
        {!editing && <button onClick={() => setEditing(true)} style={{ ...linkBtn, color: 'var(--crm-muted)' }}>Edit</button>}
      </div>
      {editing ? (
        <AddressEditor leg={leg} lead={lead} onCancel={() => setEditing(false)} onSave={(patch) => { onSaveAddress(patch); setEditing(false); }} />
      ) : (
        <div style={{ background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '6px', padding: '8px', fontSize: '12px', color: line ? 'var(--crm-text)' : 'var(--crm-dim)', minHeight: '18px' }}>
          {line || 'No address yet'}
          {check && (
            <div><button onClick={() => setEditing(true)} style={{ ...linkBtn, color: 'var(--crm-amber)', marginTop: '5px' }}>Check address</button></div>
          )}
        </div>
      )}
      {windows && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(max(120px, calc((100% - 12px) / 3)), 1fr))', gap: '6px', marginTop: '8px' }}>
          {windows}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '6px', marginTop: '8px' }}>
        <QuickEditField label="Stairs" value={stairs ?? 0} type="number" onCommit={onStairs} />
        <QuickEditField label="Walk (ft)" value={walk ?? 0} type="number" onCommit={onWalk} />
        <YesNoField label="Elevator" value={!!elevator} onChange={onElevator} />
        <YesNoField label="Shuttle" value={!!shuttle} onChange={onShuttle} />
      </div>
    </div>
  );
}

/**
 * The broken-down address (migration 052). Opens on the lead's parts; on an
 * unsplit lead it tries the splitter on the text, and leaves the text in the
 * paste box either way so the rep can see what came in. Nothing is saved until
 * Save; the server rebuilds the one line from the parts.
 */
function AddressEditor({ leg, lead, onSave, onCancel }: {
  leg: Leg; lead: any; onSave: (patch: Record<string, string | null>) => void; onCancel: () => void;
}) {
  const text: string = lead?.[`${leg}_address`] ?? '';
  const initial = (): AddressParts => {
    const stored = partsOf(lead, leg);
    if (stored.city) return stored;
    const r = splitAddress(text);
    return r.ok ? r.parts : stored;
  };
  const [p, setP] = useState<AddressParts>(initial);
  const [paste, setPaste] = useState<string>(partsOf(lead, leg).city ? '' : text);
  const [pasteMsg, setPasteMsg] = useState<string | null>(null);
  const set = (k: keyof AddressParts, v: string) => setP((o) => ({ ...o, [k]: v }));
  const canada = isCanada(p.country);

  const doSplit = () => {
    const r = splitAddress(paste);
    if (r.ok) { setP(r.parts); setPasteMsg('Split — check the fields below, then Save.'); }
    else setPasteMsg('Could not read that address. Fill in the fields below.');
  };

  const box: React.CSSProperties = { background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '8px', padding: '8px 10px' };
  const lbl: React.CSSProperties = { fontSize: '9px', fontWeight: 600, color: 'var(--crm-dim)', textTransform: 'uppercase', display: 'block', marginBottom: '4px' };
  const inp: React.CSSProperties = { width: '100%', background: 'transparent', border: 'none', color: 'var(--crm-text)', fontSize: '13px', outline: 'none', padding: 0 };
  const field = (label: string, k: keyof AddressParts, extra?: Partial<React.InputHTMLAttributes<HTMLInputElement>>) => (
    <div style={box}>
      <label style={lbl}>{label}</label>
      <input value={p[k] ?? ''} onChange={(e) => set(k, e.target.value)} style={inp} {...extra} />
    </div>
  );

  return (
    <div style={{ display: 'grid', gap: '6px' }}>
      <div style={box}>
        <label style={lbl}>Paste full address</label>
        <div style={{ display: 'flex', gap: '6px' }}>
          <input value={paste} onChange={(e) => { setPaste(e.target.value); setPasteMsg(null); }} placeholder="88 Wellesley St E, Toronto, ON M4Y 1H1" style={inp} />
          <button onClick={doSplit} disabled={!paste.trim()} style={{ padding: '3px 10px', background: 'none', border: '1px solid var(--crm-line)', color: 'var(--crm-muted)', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', flexShrink: 0 }}>Split</button>
        </div>
        {pasteMsg && <div style={{ marginTop: '5px', fontSize: '10px', color: 'var(--crm-amber)' }}>{pasteMsg}</div>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(70px, 1fr) 3fr', gap: '6px' }}>
        {field('Unit / Apt', 'unit')}
        {field('Street address', 'street')}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '6px' }}>
        {field('City', 'city')}
        <div style={box}>
          <label style={lbl}>Province</label>
          {canada ? (
            <select value={p.province ?? ''} onChange={(e) => set('province', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              <option value="" style={{ background: 'var(--crm-panel-2)' }}>—</option>
              {PROVINCES.map((pr) => <option key={pr.code} value={pr.code} style={{ background: 'var(--crm-panel-2)' }}>{pr.code} · {pr.name}</option>)}
            </select>
          ) : (
            <input value={p.province ?? ''} onChange={(e) => set('province', e.target.value)} style={inp} />
          )}
        </div>
        {field('Postal code', 'postal_code', canada ? { onBlur: (e) => set('postal_code', normalizePostal(e.target.value) ?? '') } : undefined)}
        <div style={box}>
          <label style={lbl}>Country</label>
          <input value={p.country === 'CA' || p.country == null ? 'Canada' : p.country} onChange={(e) => set('country', e.target.value)} style={inp} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{ padding: '6px 12px', background: 'none', border: '1px solid var(--crm-line)', color: 'var(--crm-muted)', borderRadius: '7px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
        <button onClick={() => onSave(addressColumns(leg, p))} style={{ padding: '6px 12px', background: 'var(--crm-amber-fill)', border: 'none', color: 'var(--crm-on-amber)', borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}>Save</button>
      </div>
    </div>
  );
}

// Yes/No chip-style selector: amber text when "Yes", muted when "No".
function YesNoField({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={{ background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '8px', padding: '10px' }}>
      <label style={{ fontSize: '9px', fontWeight: 600, color: 'var(--crm-dim)', textTransform: 'uppercase', display: 'block', marginBottom: '5px' }}>{label}</label>
      <select
        value={value ? 'Yes' : 'No'}
        onChange={(e) => onChange(e.target.value === 'Yes')}
        style={{ width: '100%', background: 'transparent', border: 'none', color: value ? 'var(--crm-amber)' : 'var(--crm-muted)', fontSize: '13px', fontWeight: value ? 600 : 400, outline: 'none', padding: 0, cursor: 'pointer' }}
      >
        <option value="No" style={{ background: 'var(--crm-panel-2)', color: 'var(--crm-text)' }}>No</option>
        <option value="Yes" style={{ background: 'var(--crm-panel-2)', color: 'var(--crm-text)' }}>Yes</option>
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Estimate engine (Phase 8c)
// ---------------------------------------------------------------------------

// What a discount popover is currently editing.
type DiscountTarget =
  | { kind: 'line'; id: string; type: DiscountType; value: number; label: string }
  | { kind: 'base'; type: DiscountType; value: number; label: string }
  | { kind: 'total'; type: DiscountType; value: number; label: string };

interface EstimatePanelProps {
  leadId: string;
  lead: any;
  tariff: any;
  onLeadChanged: () => void;
}

function EstimatePanel({ leadId, lead, tariff, onLeadChanged }: EstimatePanelProps) {
  const api = useApiClient();
  const { data: est, loading, error, refresh } = useApi<{ estimate: any; items: any[]; accessorials?: any[] }>(
    async () => (await api.getEstimate(leadId)).data!,
    { dependencies: [leadId] }
  );
  // 060: switching the tariff adds and removes default fee lines on the server.
  // Reload them once the saved lead comes back (updated_at moves only then —
  // the on-screen overlay changes tariff_id before the server has acted).
  const tariffSeen = React.useRef(lead?.tariff_id ?? null);
  useEffect(() => {
    const t = lead?.tariff_id ?? null;
    if (t === tariffSeen.current) return;
    tariffSeen.current = t;
    refresh().catch((e) => console.error('Estimate refresh after tariff switch failed:', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.updated_at]);
  const { data: fees } = useApi<any[]>(async () => (await api.getFees()).data || []);
  const { data: branches } = useApi<any[]>(async () => (await api.getBranches()).data || []);
  const { data: estCompanyArr } = useApi<any[]>(async () => (await api.getCompany()).data || []);

  const [popover, setPopover] = useState<DiscountTarget | null>(null);
  const [showFeePicker, setShowFeePicker] = useState(false);
  const [busy, setBusy] = useState(false);

  /**
   * LINE EDITS ARE ON SCREEN AT ONCE AND SAVED BEHIND (useRowSync). Each one
   * used to wait for its PATCH and then a refetch that flipped the whole panel
   * to "Loading estimate…" — ten edits were ten blank flashes.
   *
   * THE TOTALS STAY HONEST. They are computed below by calcEstimate, the same
   * engine the server prices with, from the lines on screen — so the number
   * follows each edit immediately and is the right number for those lines. But
   * it is not what the SERVER holds until the batch lands, so while anything is
   * waiting the summary says "Updating…" and dims the total; once the batch has
   * saved, one quiet refresh brings back the server's own lines (tax, fuel,
   * accessorials, DVP included) and the overlay is dropped.
   */
  const sync = useRowSync<any>({
    update: (id, patch) => api.updateEstimateItem(id, patch),
    remove: (id) => api.deleteEstimateItem(id),
    onSettled: refresh,
  });
  const items = sync.view(est?.items ?? []);
  const lineState = new Map(items.map((it: any) => [it.id, it]));
  const feeList = fees ?? [];
  const branch = branches?.find((b) => b.id === lead.branch_id) ?? null;
  const baseItem = items.find((it: any) => it.source === 'base');

  // A1: accessorial rates travel WITH the estimate payload, so this call has the
  // identical input set the server used — the two engines cannot disagree.
  const calc = calcEstimate(lead, items, tariff, branch, feeList, est?.accessorials ?? []);
  const baseLine = calc.items.find((l) => l.source === 'base')!;
  const otherLines = calc.items.filter((l) => l.source !== 'base');

  const nextSort = () => items.reduce((m: number, it: any) => Math.max(m, Number(it.sort_order) || 0), 0) + 1;

  // Wrap every mutation so we surface a spinner and never leave `busy` stuck.
  // ONE AT A TIME (useAction): a double click on "+ Add fee" used to add the
  // fee twice. The key makes a repeated add return the first line.
  const guard = useAction();
  const run = (fn: (key: string) => Promise<void>) =>
    guard.run(async (key) => {
      setBusy(true);
      try {
        await fn(key);
      } catch (e) {
        console.error('Estimate update failed:', e);
      } finally {
        setBusy(false);
      }
    });

  const commitBaseRate = (raw: string) => {
    const value = parseFloat(raw) || 0;
    run(async (key) => {
      await api.withIdempotencyKey(key).addEstimateItem(leadId, {
        source: 'base',
        rate_snapshot: value,
        description: tariff?.name ?? 'Base charge',
        sort_order: -1,
      });
      await refresh();
    });
  };
  // With a base line already there, the rate is an ordinary line edit.
  const commitBase = (raw: string) =>
    baseItem ? sync.edit(baseItem._key, { rate_snapshot: parseFloat(raw) || 0 }) : commitBaseRate(raw);

  // ---- Hourly crew (053) ---------------------------------------------------
  /**
   * HOURLY TARIFFS: trucks, movers, rate, minimum and estimated hours live on
   * the base line. Billed hours are max(estimated, minimum) — the engine does
   * that, so the Labour line, the totals and the portal all agree.
   *
   * EXTRA TRUCKS ARE A FEE LINE, the company's "Additional Truck" fee (matched
   * by name: Settings → Fees has no other link to it), quantity trucks − 1. It
   * follows a CHANGE of Trucks only: added or re-counted when Trucks moves to
   * 2+, removed when it moves back to 1. A rep who deletes the line keeps it
   * deleted until Trucks changes again.
   */
  const hourly = tariff?.pricing_method === 'hourly';
  const presets: { trucks: number; movers: number; rate: number }[] =
    hourly && Array.isArray(tariff?.crew_presets) ? tariff.crew_presets : [];
  const truckFee = findAdditionalTruckFee(feeList);

  /**
   * LOCAL DEPOSIT (056): shown to the client on the estimate portal and PDF.
   * The estimate's own amount, else the tariff's default ($150 when it has
   * none). Saved as null when it equals the default or is cleared, so a later
   * change to the tariff's default still reaches this estimate.
   */
  const depositDefault = present(tariff?.deposit_amount) ? Number(tariff.deposit_amount) : 150;
  const [depositSaved, setDepositSaved] = useState<number | null | undefined>(undefined);
  const depositStored = depositSaved !== undefined ? depositSaved : (present(est?.estimate?.deposit_amount) ? Number(est!.estimate.deposit_amount) : null);
  const depositShown = depositStored ?? depositDefault;
  const [depositError, setDepositError] = useState<string | null>(null);
  const saveDeposit = async (raw: string) => {
    const n = raw.trim() === '' ? null : Number(raw);
    if (n != null && (!Number.isFinite(n) || n < 0)) { setDepositError('enter an amount of $0 or more'); return; }
    const value = n == null || n === depositDefault ? null : Math.round(n * 100) / 100;
    if (!est?.estimate?.id) return;
    setDepositError(null);
    setDepositSaved(value);
    try {
      await api.updateEstimate(est.estimate.id, { deposit_amount: value });
    } catch (e: any) {
      setDepositSaved(undefined);
      setDepositError(e?.message || 'save failed');
    }
  };
  const truckLine = truckFee ? items.find((it: any) => it.fee_id === truckFee.id) : undefined;

  // What the Additional Truck line needs for `trucks`: add, re-count, remove, or nothing.
  const truckFeeFor = (trucks: number) => {
    if (!truckFee) return null;
    if (trucks >= 2) {
      if (truckLine) return Number(truckLine.quantity) === trucks - 1 ? null : { kind: 'qty' as const, qty: trucks - 1 };
      return { kind: 'add' as const, qty: trucks - 1 };
    }
    return truckLine ? { kind: 'remove' as const } : null;
  };

  const saveHourly = (patch: Record<string, number | null>) => {
    const trucksChanged = 'trucks' in patch && (patch.trucks ?? null) !== (present(baseItem?.trucks) ? Number(baseItem.trucks) : null);
    const truckStep = trucksChanged ? truckFeeFor(Number(patch.trucks) || 1) : null;
    const addTruck = (a: typeof api, qty: number) =>
      a.addEstimateItem(leadId, { fee_id: truckFee!.id, source: 'fee', quantity: qty, sort_order: nextSort() }, true);

    if (baseItem) {
      sync.edit(baseItem._key, patch);
      if (truckStep?.kind === 'qty') sync.edit(truckLine._key, { quantity: truckStep.qty });
      else if (truckStep?.kind === 'remove') sync.remove(truckLine._key);
      else if (truckStep?.kind === 'add') run(async (key) => { await addTruck(api.withIdempotencyKey(key), truckStep.qty); await refresh(); });
      return;
    }
    // No base line yet: create it with these values (and the rate on screen,
    // which would otherwise be stored as 0), then the truck line.
    run(async (key) => {
      await api.withIdempotencyKey(key).addEstimateItem(leadId, {
        source: 'base',
        description: tariff?.name ?? 'Base charge',
        sort_order: -1,
        rate_snapshot: baseLine.rate,
        ...patch,
      });
      if (truckStep?.kind === 'add') await addTruck(api, truckStep.qty);
      await refresh();
    });
  };

  const pickPreset = (p: { trucks: number; movers: number; rate: number }) =>
    saveHourly({ trucks: p.trucks, crew_size: p.movers, rate_snapshot: p.rate });
  const wholeOrNull = (raw: string) => (raw.trim() === '' || !(Number(raw) >= 1) ? null : Math.round(Number(raw)));
  const quarterOrNull = (raw: string) => (raw.trim() === '' || !(Number(raw) > 0) ? null : Math.round(Number(raw) * 4) / 4);

  // TRAVEL AND ON-SITE, ENTERED APART (054), IN HOURS AND MINUTES. Worked in
  // whole minutes here; stored as hours to the minute (4 h 25 = 4.41666…), so
  // they come back exactly. `hours` stays the total — travel + on-site — and the
  // engine bills it rounded UP to 15 minutes, never below the minimum, so travel
  // counts toward the minimum. A line saved before the split has hours and no
  // travel_hours: shown as 0 travel and all of it on site, unchanged until edited.
  const toMin = (h: number) => Math.round(h * 60);
  const totalMin = baseLine.estimatedHours != null ? toMin(baseLine.estimatedHours) : null;
  const travelStoredMin = present(baseItem?.travel_hours) ? toMin(Number(baseItem.travel_hours)) : null;
  const travelMin = travelStoredMin ?? (totalMin != null ? 0 : null);
  const onsiteMin = totalMin != null ? Math.max(0, totalMin - (travelStoredMin ?? 0)) : null;
  const saveSplit = (travel: number | null, onsite: number | null) => {
    const total = (travel ?? 0) + (onsite ?? 0);
    saveHourly({ travel_hours: travel == null ? null : travel / 60, hours: total > 0 ? total / 60 : null });
  };

  // ---- Any line's rate (053) -----------------------------------------------
  // A fee line's rate, edited here, is that line's from now on (rate_overridden:
  // the engine prices from rate_snapshot — $, $/unit or %). ↺ gives it back to
  // the fee. An access line edited this way stays priced from its rate row (its
  // minimum and free allowance kept) at the new rate.
  const commitLineRate = (line: CalcLine, raw: string) => {
    if (!line.id) return;
    const st: any = lineState.get(line.id);
    if (line.source === 'custom' || !(st?.fee_id || st?.auto_key)) {
      setLineField(line.id, { rate_snapshot: parseFloat(raw) || 0 });
      return;
    }
    setLineField(line.id, { rate_snapshot: parseFloat(raw) || 0, rate_overridden: true, ...(st?.auto_key ? { auto_edited: true } : {}) });
  };
  const resetLineRate = (line: CalcLine) => {
    if (!line.id) return;
    const st: any = lineState.get(line.id);
    setLineField(line.id, { rate_overridden: false, ...(st?.auto_key ? { auto_edited: false } : {}) });
  };

  const setLineField = (id: string, patch: Record<string, any>) =>
    sync.edit(lineState.get(id)?._key ?? id, patch);

  const removeLine = (id: string) => sync.remove(lineState.get(id)?._key ?? id);

  // A1 — the server WARNS (409 DUPLICATE_ACCESSORIAL) when this fee duplicates a
  // charge the access conditions already added. It never blocks: confirming adds
  // the line anyway, because a second shuttle sometimes genuinely happens. What
  // must not happen is billing a client twice without anyone noticing.
  const addFee = (fee: any) =>
    run(async (key) => {
      const payload = { fee_id: fee.id, source: 'fee', quantity: 1, sort_order: nextSort() };
      const keyed = api.withIdempotencyKey(key);
      try {
        await keyed.addEstimateItem(leadId, payload);
      } catch (e: any) {
        if (e?.code !== 'DUPLICATE_ACCESSORIAL') throw e;
        if (!window.confirm(e.message)) return;
        await keyed.addEstimateItem(leadId, payload, true); // the refused attempt released the key
      }
      setShowFeePicker(false);
      await refresh();
    });

  // An access fee picked by hand: the server gives it the rate's key and the
  // added_manually flag, so it prices from the tariff and the access sync
  // leaves it alone. Already on the estimate → the picker shows it as Added.
  const addAccessFee = (rate: any) =>
    run(async (key) => {
      await api.withIdempotencyKey(key).addEstimateItem(leadId, {
        auto_key: accessorialKey(rate.leg, rate.accessorial),
        source: 'fee',
        sort_order: nextSort(),
      });
      setShowFeePicker(false);
      await refresh();
    });

  const addCustom = () =>
    run(async (key) => {
      await api.withIdempotencyKey(key).addEstimateItem(leadId, {
        source: 'custom',
        description: '',
        rate_snapshot: 0,
        quantity: 1,
        sort_order: nextSort(),
      });
      await refresh();
    });

  // ---- Coverage (061): the office's DVP election and its one premium line ----
  // Errors are the panel's to show (it offers to convert a hand-made valuation
  // line), so they are not swallowed the way run() swallows them.
  const saveCoverage = async (payload: CoverageSave) => {
    setBusy(true);
    try {
      await sync.flushAll();
      await api.setCoverage(leadId, payload);
      // A contract out for signature may now show a different election — the
      // Documents section re-checks (its "Regenerate contract" warning).
      window.dispatchEvent(new CustomEvent('crm:coverage-changed', { detail: { leadId } }));
      onLeadChanged();
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const applyDiscount = (target: DiscountTarget, type: DiscountType, value: number) =>
    run(async () => {
      if (target.kind === 'line') {
        // A line field like any other: on screen now, saved behind.
        setLineField(target.id, { discount_type: type, discount_value: value });
      } else if (target.kind === 'base') {
        await api.updateEstimateDiscounts(leadId, { base_discount_type: type, base_discount_value: value });
        onLeadChanged();
        await refresh();
      } else {
        await api.updateEstimateDiscounts(leadId, { total_discount_type: type, total_discount_value: value });
        onLeadChanged();
        await refresh();
      }
      setPopover(null);
    });

  if (loading) return <div style={{ padding: '20px', color: 'var(--crm-muted)', fontSize: '12px' }}>Loading estimate…</div>;
  if (error) return <div style={{ padding: '20px', color: 'var(--crm-red)', fontSize: '12px' }}>Failed to load estimate: {error}</div>;

  const th: React.CSSProperties = {
    textAlign: 'left',
    fontSize: '9px',
    fontWeight: 700,
    color: 'var(--crm-dim)',
    textTransform: 'uppercase',
    padding: '8px 10px',
    borderBottom: '1px solid var(--crm-line)',
  };
  const td: React.CSSProperties = { padding: '9px 10px', fontSize: '12px', color: 'var(--crm-text)', borderBottom: '1px solid var(--crm-panel-2)', verticalAlign: 'middle' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ fontSize: '11px', fontWeight: 700, color: 'var(--crm-dim)', textTransform: 'uppercase' }}>Estimate & Pricing</h3>
        <button
          onClick={() => { void sync.flushAll().then(() => refresh()); onLeadChanged(); }}
          disabled={busy}
          style={{ padding: '5px 11px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', color: 'var(--crm-muted)', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
        >
          ↻ Recalculate
        </button>
      </div>

      <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {/* Line items */}
        <div style={{ flex: '1 1 460px', minWidth: '320px', background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '10px', overflow: 'hidden' }}>
          <div style={{ padding: '11px 14px', background: 'var(--crm-panel-2)', borderBottom: '1px solid var(--crm-line)', fontSize: '12px', fontWeight: 600 }}>
            TARIFF: <span style={{ color: 'var(--crm-accent)' }}>{tariff?.name || '—'}</span>
            {tariff && (
              <span style={{ color: 'var(--crm-dim)', fontFamily: "'DM Mono', monospace", marginLeft: '8px' }}>
                · ${Number(tariff.base_rate ?? 0).toFixed(2)}
                {tariff.pricing_method === 'weight' ? '/lb' : tariff.pricing_method === 'cuft' ? '/cuft' : ''}
              </span>
            )}
          </div>

          {hourly && (
            <HourlyCrewPanel
              presets={presets}
              trucks={baseLine.trucks ?? null}
              movers={baseLine.crewSize ?? null}
              rate={baseLine.rate}
              minimumHours={baseLine.minimumHours ?? null}
              travelMinutes={travelMin}
              onsiteMinutes={onsiteMin}
              totalMinutes={totalMin}
              billedHours={baseLine.unitLabel === 'hr' ? baseLine.quantity : null}
              minimumApplies={atMinimum(baseLine)}
              truckFeeMissing={!truckFee}
              deposit={depositShown}
              depositDefault={depositDefault}
              depositError={depositError}
              onDeposit={saveDeposit}
              onPreset={pickPreset}
              onTrucks={(v) => saveHourly({ trucks: wholeOrNull(v) })}
              onMovers={(v) => saveHourly({ crew_size: wholeOrNull(v) })}
              onRate={(v) => saveHourly({ rate_snapshot: parseFloat(v) || 0 })}
              onMinimum={(v) => saveHourly({ minimum_hours: quarterOrNull(v) })}
              onTravel={(m) => saveSplit(m, onsiteMin)}
              onOnsite={(m) => saveSplit(travelStoredMin, m)}
            />
          )}

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Description</th>
                <th style={{ ...th, textAlign: 'right', width: '70px' }}>Qty</th>
                <th style={{ ...th, textAlign: 'right', width: '130px' }}>Rate</th>
                <th style={{ ...th, textAlign: 'right', width: '100px' }}>Amount</th>
                <th style={{ ...th, width: '30px' }}></th>
                <th style={{ ...th, width: '26px' }}></th>
              </tr>
            </thead>
            <tbody>
              {/* Base line */}
              <tr style={baseItem?._status === 'error' ? { background: 'var(--crm-red-tint)' } : undefined}>
                <td style={td}>
                  {baseLine.desc}
                  {hourly && crewText(baseLine.crewSize, baseLine.trucks) && (
                    <span style={{ color: 'var(--crm-muted)' }}> · {crewText(baseLine.crewSize, baseLine.trucks)}</span>
                  )}
                  {baseItem?._status === 'error' && (
                    <LineError message={baseItem._error} onRetry={() => sync.retry(baseItem._key)} />
                  )}
                </td>
                <td
                  style={{ ...td, textAlign: 'right', color: 'var(--crm-muted)', fontFamily: "'DM Mono', monospace" }}
                  title={atMinimum(baseLine) ? `Billed at the ${baseLine.minimumHours} hr minimum (estimated ${hm(Math.round((baseLine.estimatedHours ?? 0) * 60))})` : undefined}
                >
                  {baseLine.unitLabel ? baseLine.quantity.toLocaleString() : '—'}
                  {atMinimum(baseLine) && <div style={{ fontSize: '10px', color: 'var(--crm-amber)' }}>min</div>}
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <InlineInput
                    value={baseLine.rate}
                    type="number"
                    step={baseLine.unitLabel === 'lb' ? STEP_PER_LB : 1}
                    align="right"
                    prefix="$"
                    suffix={baseLine.unitLabel === 'hr' ? '/hr' : undefined}
                    onCommit={commitBase}
                  />
                </td>
                <td style={{ ...td, textAlign: 'right', fontFamily: "'DM Mono', monospace" }}>
                  <AmountCell line={baseLine} />
                </td>
                <td style={{ ...td, textAlign: 'center' }}>
                  <DiscountIcon
                    active={!!baseLine.discountType}
                    onClick={() => setPopover({ kind: 'base', type: baseLine.discountType, value: baseLine.discountValue, label: 'Base discount' })}
                  />
                </td>
                <td style={td}></td>
              </tr>

              {/* Fee / custom lines */}
              {otherLines.map((line) => {
                const st: any = line.id ? lineState.get(line.id) : null;
                // 061: the DVP premium line is priced under Coverage, not here.
                const dvpManaged = !!st?.dvp_managed;
                return (
                <tr key={st?._key ?? line.id ?? line.desc} style={st?._status === 'error' ? { background: 'var(--crm-red-tint)' } : undefined}>
                  <td style={td}>
                    {dvpManaged ? (
                      <span title="Set under Coverage below">
                        {line.desc}
                        <span style={{ marginLeft: '6px', fontSize: '9px', fontWeight: 700, color: 'var(--crm-green)', border: '1px solid var(--crm-green-soft-line)', borderRadius: '4px', padding: '1px 5px' }}>COVERAGE</span>
                      </span>
                    ) : line.source === 'custom' ? (
                      <InlineInput
                        value={line.desc}
                        placeholder="Description"
                        onCommit={(v) => line.id && setLineField(line.id, { description: v })}
                      />
                    ) : (
                      line.desc
                    )}
                    {st?._status === 'error' && (
                      <LineError message={st._error} onRetry={() => sync.retry(st._key)} />
                    )}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {line.rateKind === 'pct' || dvpManaged ? (
                      <span style={{ color: 'var(--crm-muted)' }}>{dvpManaged ? line.quantity : '—'}</span>
                    ) : (
                      <InlineInput
                        value={line.quantity}
                        type="number"
                        align="right"
                        onCommit={(v) => line.id && setLineField(line.id, { quantity: parseFloat(v) || 0 })}
                      />
                    )}
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: "'DM Mono', monospace" }}>
                    {/* Every line's rate is editable (053). A fee line's edit is
                        amber with ↺ to give the rate back to the fee. */}
                    {dvpManaged ? formatMoney(line.rate) : <InlineInput
                      value={line.rate}
                      type="number"
                      step={line.rateKind === 'unit' && line.unitLabel === 'lb' ? STEP_PER_LB : 1}
                      negative={line.source === 'custom'}
                      align="right"
                      prefix={line.rateKind === 'pct' ? undefined : '$'}
                      suffix={line.rateKind === 'pct' ? '%' : line.rateKind === 'unit' ? `/${line.unitLabel ?? 'unit'}` : undefined}
                      highlight={!!line.rateOverridden}
                      onCommit={(v) => commitLineRate(line, v)}
                    />}
                    {line.rateOverridden && (
                      <button
                        onClick={() => resetLineRate(line)}
                        title="Edited on this estimate. Reset to the fee's rate"
                        aria-label="Reset to the fee's rate"
                        style={{ background: 'none', border: 'none', color: 'var(--crm-amber)', cursor: 'pointer', fontSize: '13px', padding: '0 0 0 4px' }}
                      >
                        ↺
                      </button>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: "'DM Mono', monospace" }}>
                    <AmountCell line={line} />
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    {!dvpManaged && <DiscountIcon
                      active={!!line.discountType}
                      onClick={() =>
                        line.id &&
                        setPopover({ kind: 'line', id: line.id, type: line.discountType, value: line.discountValue, label: line.desc })
                      }
                    />}
                  </td>
                  <td style={{ ...td, textAlign: 'center' }}>
                    {!dvpManaged && <button
                      onClick={() => line.id && removeLine(line.id)}
                      title="Remove line"
                      style={{ background: 'none', border: 'none', color: 'var(--crm-dim)', cursor: 'pointer', fontSize: '13px', padding: 0, width: '36px', height: '36px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Icons.close size={14} aria-label="Remove line" />
                    </button>}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{ display: 'flex', gap: '8px', padding: '10px 12px', borderTop: '1px solid var(--crm-line)' }}>
            <button
              onClick={() => setShowFeePicker(true)}
              disabled={busy}
              style={{ padding: '6px 11px', background: 'var(--crm-accent-fill)', color: 'var(--crm-on-fill)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
            >
              + Add fee
            </button>
            <button
              onClick={addCustom}
              disabled={busy}
              style={{ padding: '6px 11px', background: 'var(--crm-panel-2)', color: 'var(--crm-muted)', border: '1px solid var(--crm-line)', borderRadius: '6px', cursor: 'pointer', fontSize: '11px' }}
            >
              + Add custom line
            </button>
          </div>
        </div>

        {/* Summary card */}
        <div style={{ flex: '0 0 240px', background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '10px', padding: '14px' }}>
          <div style={{ fontSize: '9px', fontWeight: 700, color: 'var(--crm-dim)', textTransform: 'uppercase', marginBottom: '12px' }}>Summary</div>

          <SummaryRow label="Subtotal" value={formatMoney(calc.subtotal)} />

          {lead.total_discount_type ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '6px 0', fontSize: '12px' }}>
                <button
                  onClick={() => setPopover({ kind: 'total', type: lead.total_discount_type, value: Number(lead.total_discount_value) || 0, label: 'Total discount' })}
                  style={{ background: 'none', border: 'none', color: 'var(--crm-amber)', cursor: 'pointer', fontSize: '12px', padding: 0, textAlign: 'left' }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>Total discount <Icons.edit size={12} /></span>
                </button>
                <span style={{ color: 'var(--crm-amber)', fontFamily: "'DM Mono', monospace" }}>−{formatMoney(calc.totalDiscountAmt)}</span>
              </div>
              <SummaryRow label="Discounted subtotal" value={formatMoney(calc.discountedSubtotal)} />
            </>
          ) : (
            <button
              onClick={() => setPopover({ kind: 'total', type: null, value: 0, label: 'Total discount' })}
              style={{ background: 'none', border: 'none', color: 'var(--crm-accent)', cursor: 'pointer', fontSize: '11px', padding: '4px 0', display: 'block' }}
            >
              + Add total discount
            </button>
          )}

          <SummaryRow
            label={`Tax (${branch?.name ?? '—'} ${calc.taxRate}%)`}
            value={formatMoney(calc.tax)}
          />

          <div style={{ borderTop: '1px solid var(--crm-line)', marginTop: '10px', paddingTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: '12px', color: 'var(--crm-muted)', fontWeight: 600 }}>Total</span>
            <span
              title={sync.busy ? 'Recalculated from the lines on screen; confirming with the server' : undefined}
              style={{ fontSize: '20px', color: 'var(--crm-green)', fontWeight: 700, fontFamily: "'DM Mono', monospace", opacity: sync.busy || sync.failed ? 0.55 : 1 }}
            >
              {formatMoney(calc.total)}
            </span>
          </div>
          {/* Never a stale total presented as final: while a line is unsaved,
              or failed, the summary says so. */}
          {(sync.busy || sync.failed > 0) && (
            <div style={{ marginTop: '6px', fontSize: '11px', textAlign: 'right', color: sync.failed ? 'var(--crm-red)' : 'var(--crm-amber)' }}>
              {sync.failed ? `${sync.failed} line${sync.failed === 1 ? '' : 's'} not saved — totals not final` : 'Updating…'}
            </div>
          )}
        </div>
      </div>

      {dvpOffered(lead) && (
        <CoveragePanel lead={lead} company={estCompanyArr?.[0] ?? {}} items={est?.items ?? []} busy={busy} onSave={saveCoverage} />
      )}

      {popover && (
        <DiscountPopover
          target={popover}
          onApply={(type, value) => applyDiscount(popover, type, value)}
          onRemove={() => applyDiscount(popover, null, 0)}
          onCancel={() => setPopover(null)}
        />
      )}

      {showFeePicker && (
        <FeePicker
          fees={feeList}
          accessRates={(est?.accessorials ?? []).filter((r: any) => r.active !== false)}
          items={items}
          onPick={addFee}
          onPickAccess={addAccessFee}
          onCancel={() => setShowFeePicker(false)}
        />
      )}
    </div>
  );
}

/** A line whose save failed: the edit stays on screen, this says so, Retry resends. */
function LineError({ message, onRetry }: { message: string | null; onRetry: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', fontSize: '11px', color: 'var(--crm-red)' }}>
      <span>Not saved{message ? ` — ${message}` : ''}</span>
      <button
        onClick={onRetry}
        style={{ minHeight: '30px', padding: '4px 10px', background: 'transparent', border: '1px solid var(--crm-red-line)', color: 'var(--crm-red)', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 700 }}
      >
        Retry
      </button>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', margin: '6px 0', fontSize: '12px' }}>
      <span style={{ color: 'var(--crm-muted)' }}>{label}</span>
      <span style={{ color: 'var(--crm-text)', fontFamily: "'DM Mono', monospace" }}>{value}</span>
    </div>
  );
}

const present = (v: unknown): boolean => v != null && v !== '';
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;
/** "3 movers, 1 truck" — whichever of the two is recorded, or ''. */
const crewText = (movers: number | null | undefined, trucks: number | null | undefined) =>
  [movers ? plural(movers, 'mover') : '', trucks ? plural(trucks, 'truck') : ''].filter(Boolean).join(', ');

/** The hourly line is billed at its minimum: more than the estimated hours. */
const atMinimum = (l: CalcLine) =>
  l.minimumHours != null && l.estimatedHours != null && l.minimumHours > roundUpToQuarterHour(l.estimatedHours);

/** 305 → "5 h 05 min". */
const hm = (minutes: number) => `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')} min`;

const money0 = (n: number) => `$${Number.isInteger(n) ? n : n.toFixed(2)}`;

// The hourly panel's own small styles: two cards side by side from 900px,
// stacked below (inline styles cannot hold a media query).
const HOURLY_CSS = `
.ldp-hourly { display:grid; grid-template-columns:minmax(0, 1fr); gap:10px; padding:12px 14px; border-bottom:1px solid var(--crm-line); }
@media (min-width:900px) { .ldp-hourly { grid-template-columns:repeat(2, minmax(0, 1fr)); } }
`;

/**
 * The hourly panel on an estimate (053 / 054), two cards:
 *   CREW & RATE — a Crew dropdown of the tariff's presets (picking one fills
 *     trucks, movers and rate, as the chips did, Additional Truck included),
 *     "Custom" when the line matches none; then the three values, editable.
 *   TIME — travel and on-site in hours + minutes, the minimum in hours, and
 *     the total with what it bills: rounded up to 15 minutes, never below the
 *     minimum.
 */
function HourlyCrewPanel(p: {
  presets: { trucks: number; movers: number; rate: number }[];
  trucks: number | null;
  movers: number | null;
  rate: number;
  minimumHours: number | null;
  travelMinutes: number | null;
  onsiteMinutes: number | null;
  totalMinutes: number | null;
  /** The Labour line's billed hours: the total rounded up to 15 min, never below the minimum. */
  billedHours: number | null;
  /** The minimum is what is billed (it is more than the rounded total). */
  minimumApplies: boolean;
  truckFeeMissing: boolean;
  /** Local deposit (056): this estimate's amount, the tariff default, and a save error. */
  deposit: number;
  depositDefault: number;
  depositError: string | null;
  onDeposit: (v: string) => void;
  onPreset: (preset: { trucks: number; movers: number; rate: number }) => void;
  onTrucks: (v: string) => void;
  onMovers: (v: string) => void;
  onRate: (v: string) => void;
  onMinimum: (v: string) => void;
  onTravel: (minutes: number) => void;
  onOnsite: (minutes: number) => void;
}) {
  const match = p.presets.findIndex((c) => c.trucks === p.trucks && c.movers === p.movers && Number(c.rate) === Number(p.rate));
  const small = (label: string, input: React.ReactNode) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      <span style={fieldLabel}>{label}</span>
      {input}
    </label>
  );
  const timeRow = (label: string, input: React.ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ ...fieldLabel, width: '58px', flexShrink: 0 }}>{label}</span>
      {input}
    </div>
  );
  return (
    <div className="ldp-hourly">
      <style>{HOURLY_CSS}</style>

      <div style={cardStyle}>
        <div style={cardTitle}>Crew &amp; rate</div>
        {p.presets.length > 0 ? (
          <label style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <span style={fieldLabel}>Crew</span>
            <select
              value={match >= 0 ? String(match) : 'custom'}
              onChange={(e) => { const c = p.presets[Number(e.target.value)]; if (c) p.onPreset(c); }}
              style={{ width: '100%', minHeight: '36px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '6px', color: 'var(--crm-text)', fontSize: '12px', padding: '6px 8px', outline: 'none', cursor: 'pointer' }}
            >
              <option value="custom" disabled style={{ background: 'var(--crm-panel-2)' }}>Custom</option>
              {p.presets.map((c, i) => (
                <option key={i} value={String(i)} style={{ background: 'var(--crm-panel-2)', color: 'var(--crm-text)' }}>
                  {plural(c.trucks, 'truck')} · {plural(c.movers, 'mover')} · {money0(Number(c.rate))}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <div style={{ fontSize: '11px', color: 'var(--crm-dim)' }}>No crew presets on this tariff (Settings → Tariffs → Crew presets).</div>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {small('Trucks', <InlineInput value={p.trucks ?? ''} type="number" step={1} width="52px" onCommit={p.onTrucks} />)}
          {small('Movers', <InlineInput value={p.movers ?? ''} type="number" step={1} width="52px" onCommit={p.onMovers} />)}
          {small('Rate / hr', <InlineInput value={p.rate} type="number" step={1} prefix="$" width="76px" onCommit={p.onRate} />)}
          {small('Deposit', <InlineInput value={p.deposit} type="number" step={1} prefix="$" width="76px" highlight={p.deposit !== p.depositDefault} onCommit={p.onDeposit} />)}
        </div>
        {p.deposit !== p.depositDefault && (
          <div style={{ fontSize: '11px', color: 'var(--crm-muted)' }}>Deposit changed from the tariff's {money0(p.depositDefault)}. Clear it to go back.</div>
        )}
        {p.depositError && <div style={{ fontSize: '11px', color: 'var(--crm-red)' }}>Deposit not saved — {p.depositError}</div>}
        {p.truckFeeMissing && (p.trucks ?? 0) >= 2 && (
          <div style={{ fontSize: '11px', color: 'var(--crm-amber)' }}>
            No "Additional Truck" fee in Settings → Fees, so the extra truck is not charged. Add the fee, or a line by hand.
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <div style={cardTitle}>Time</div>
        {timeRow('Travel', <HoursMinutesInput minutes={p.travelMinutes} onCommit={p.onTravel} />)}
        {timeRow('On-site', <HoursMinutesInput minutes={p.onsiteMinutes} onCommit={p.onOnsite} />)}
        {timeRow('Minimum', (
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <InlineInput value={p.minimumHours ?? ''} type="number" step={0.25} width="56px" onCommit={p.onMinimum} />
            <span style={unitLabel}>h</span>
          </span>
        ))}
        <div style={{ borderTop: '1px solid var(--crm-line)', paddingTop: '9px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '11px', color: 'var(--crm-muted)' }}>
            Total {p.totalMinutes != null ? hmShort(p.totalMinutes) : '—'}
            {p.minimumApplies && ' · minimum applies'}
          </span>
          <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--crm-text)' }}>
            {p.billedHours != null ? `${p.billedHours.toFixed(2)} hrs billed` : '—'}
          </span>
        </div>
      </div>
    </div>
  );
}

// Shows the net amount; when a line discount is applied the gross is struck through.
function AmountCell({ line }: { line: CalcLine }) {
  if (line.discountAmt > 0) {
    return (
      <span>
        <span style={{ color: 'var(--crm-dim)', textDecoration: 'line-through', marginRight: '6px', fontSize: '11px' }}>{formatMoney(line.gross)}</span>
        {formatMoney(line.net)}
      </span>
    );
  }
  return <span>{formatMoney(line.net)}</span>;
}

function DiscountIcon({ active, onClick }: { active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title={active ? 'Discount applied' : 'Add discount'}
      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', padding: 0, width: '36px', height: '36px', color: active ? 'var(--crm-amber)' : 'var(--crm-dim)' }}
    >
      %
    </button>
  );
}

// Centered modal so it is never clipped by an overflow:hidden ancestor.
function ModalShell({ children, onCancel }: { children: React.ReactNode; onCancel: () => void }) {
  return (
    <div
      onClick={onCancel}
      style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '12px', padding: '18px', minWidth: '260px', boxShadow: '0 20px 60px var(--crm-shadow-color)' }}>
        {children}
      </div>
    </div>
  );
}

function DiscountPopover({
  target,
  onApply,
  onRemove,
  onCancel,
}: {
  target: DiscountTarget;
  onApply: (type: DiscountType, value: number) => void;
  onRemove: () => void;
  onCancel: () => void;
}) {
  const [type, setType] = useState<'flat' | 'pct'>(target.type === 'pct' ? 'pct' : 'flat');
  const [value, setValue] = useState<string>(String(target.value ?? 0));

  return (
    <ModalShell onCancel={onCancel}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--crm-text)', marginBottom: '12px' }}>Discount · <span style={{ color: 'var(--crm-muted)', fontWeight: 400 }}>{target.label}</span></div>

      <div style={{ display: 'flex', gap: '6px', marginBottom: '10px' }}>
        {(['flat', 'pct'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setType(t)}
            style={{
              flex: 1,
              padding: '6px',
              borderRadius: '6px',
              border: '1px solid var(--crm-line)',
              background: type === t ? 'var(--crm-accent-fill)' : 'var(--crm-panel-2)',
              color: type === t ? 'var(--crm-on-fill)' : 'var(--crm-muted)',
              cursor: 'pointer',
              fontSize: '12px',
            }}
          >
            {t === 'flat' ? '$ Amount' : '% Percent'}
          </button>
        ))}
      </div>

      <input
        {...numberInput(1)}
        value={value}
        autoFocus
        onChange={(e) => setValue(e.target.value)}
        style={{ width: '100%', boxSizing: 'border-box', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '6px', color: 'var(--crm-text)', fontSize: '13px', padding: '7px 9px', marginBottom: '12px', fontFamily: "'DM Mono', monospace" }}
      />

      <div style={{ display: 'flex', gap: '6px' }}>
        <button
          onClick={() => onApply(type, parseFloat(value) || 0)}
          style={{ flex: 1, padding: '7px', background: 'var(--crm-green-fill)', color: 'var(--crm-on-fill)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
        >
          Apply
        </button>
        <button
          onClick={onRemove}
          style={{ padding: '7px 11px', background: 'var(--crm-panel-2)', color: 'var(--crm-red)', border: '1px solid var(--crm-line)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
        >
          Remove
        </button>
        <button
          onClick={onCancel}
          style={{ padding: '7px 11px', background: 'var(--crm-panel-2)', color: 'var(--crm-muted)', border: '1px solid var(--crm-line)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
        >
          Cancel
        </button>
      </div>
    </ModalShell>
  );
}

const ACCESS_NAME: Record<string, string> = { elevator: 'Elevator', stairs: 'Stairs', long_walk: 'Long walk', shuttle: 'Shuttle' };
const LEG_NAME: Record<string, string> = { origin: 'Origin', destination: 'Destination' };

/** "$150.00", "$75.00/flight", "15%" — the rate as the picker shows it. */
function rateText(t: { method?: string | null; amount?: any; rate?: any; percentage?: any; unit_label?: string | null }) {
  if (t.method === 'pct') return `${Number(t.percentage ?? 0)}%`;
  if (t.method === 'unit') return `$${Number(t.rate ?? 0).toFixed(2)}/${t.unit_label ?? 'unit'}`;
  return `$${Number(t.amount ?? 0).toFixed(2)}`;
}

/**
 * "Add a fee": three collapsible groups, all closed until clicked, so a new rep
 * sees three names rather than a long list. A fee already on the estimate —
 * added by the access sync or by hand — shows greyed as "Added" and does nothing.
 */
function FeePicker({ fees, accessRates, items, onPick, onPickAccess, onCancel }: {
  fees: any[];
  accessRates: any[];
  items: any[];
  onPick: (fee: any) => void;
  onPickAccess: (rate: any) => void;
  onCancel: () => void;
}) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const onEstimateKeys = new Set(items.map((it: any) => it.auto_key).filter(Boolean));
  const onEstimateFees = new Set(items.map((it: any) => it.fee_id).filter(Boolean));

  type Row = { key: string; name: string; rate: string; added: boolean; pick: () => void };
  const groups: Array<{ id: string; title: string; rows: Row[]; empty: string }> = [
    {
      id: 'access',
      title: 'Access fees',
      empty: 'No access fees set for this tariff',
      rows: accessRates.map((r) => {
        const key = accessorialKey(r.leg, r.accessorial);
        return {
          key,
          name: `${ACCESS_NAME[r.accessorial] ?? r.accessorial} · ${LEG_NAME[r.leg] ?? r.leg}`,
          rate: rateText({ method: r.pricing_method, amount: r.amount, rate: r.rate, percentage: r.percentage, unit_label: r.unit_label }),
          added: onEstimateKeys.has(key),
          pick: () => onPickAccess(r),
        };
      }),
    },
    ...[
      { id: 'additional', title: 'Additional fees', list: fees.filter((f) => f.category !== 'materials') },
      { id: 'materials', title: 'Materials', list: fees.filter((f) => f.category === 'materials') },
    ].map((g) => ({
      id: g.id,
      title: g.title,
      empty: 'None configured.',
      rows: g.list.map((fee) => ({
        key: fee.id,
        name: fee.name,
        rate: rateText({ method: fee.fee_type, amount: fee.amount, rate: fee.rate, percentage: fee.percentage, unit_label: fee.unit_label }),
        added: onEstimateFees.has(fee.id),
        pick: () => onPick(fee),
      })),
    })),
  ];

  return (
    <ModalShell onCancel={onCancel}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--crm-text)', marginBottom: '12px' }}>Add a fee</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '380px', overflowY: 'auto', minWidth: '280px' }}>
        {groups.map((g) => (
          <div key={g.id} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <button
              onClick={() => setOpen((o) => ({ ...o, [g.id]: !o[g.id] }))}
              aria-expanded={!!open[g.id]}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 11px', background: 'transparent', border: '1px solid var(--crm-line)', borderRadius: '7px', cursor: 'pointer', color: 'var(--crm-text)', fontSize: '12px', fontWeight: 600, textAlign: 'left' }}
            >
              <span>{g.title} <span style={{ color: 'var(--crm-muted)', fontWeight: 400 }}>({g.rows.length})</span></span>
              <span style={{ color: 'var(--crm-muted)' }}>{open[g.id] ? '▾' : '▸'}</span>
            </button>
            {open[g.id] && g.rows.length === 0 && (
              <div style={{ color: 'var(--crm-dim)', fontSize: '12px', padding: '4px 11px 8px' }}>{g.empty}</div>
            )}
            {open[g.id] && g.rows.map((row) => (
              <button
                key={row.key}
                onClick={row.added ? undefined : row.pick}
                disabled={row.added}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 11px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '7px', cursor: row.added ? 'default' : 'pointer', color: 'var(--crm-text)', fontSize: '12px', textAlign: 'left', opacity: row.added ? 0.45 : 1 }}
              >
                <span>{row.name}</span>
                <span style={{ color: 'var(--crm-muted)', fontFamily: "'DM Mono', monospace", fontSize: '11px' }}>
                  {row.added ? 'Added' : row.rate}
                </span>
              </button>
            ))}
          </div>
        ))}
      </div>
      <button
        onClick={onCancel}
        style={{ marginTop: '12px', width: '100%', padding: '7px', background: 'var(--crm-panel-2)', color: 'var(--crm-muted)', border: '1px solid var(--crm-line)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}
      >
        Cancel
      </button>
    </ModalShell>
  );
}
